import axios from 'axios';
import { User, Document, FieldDefinition, DocumentField, AuditLog, Notification, Category, CategoryFieldDefinition } from '../types';

// Determine API host dynamically based on the current browser location.
// This allows the app to work seamlessly on:
//   - localhost (development)
//   - Tailscale IPs (e.g. http://100.x.x.x:3000)
//   - Port-forwarded domains
// Override with REACT_APP_API_URL env var if backend is on a completely different host/port.
const getApiHost = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, '');
  }
  // When served via reverse proxy (Caddy) on standard ports (80/443),
  // API is on the same origin — no need to specify a different port.
  const port = window.location.port;
  if (!port || port === '80' || port === '443') {
    return `${window.location.protocol}//${window.location.hostname}`;
  }
  const backendPort = process.env.REACT_APP_API_PORT || '8000';
  return `${window.location.protocol}//${window.location.hostname}:${backendPort}`;
};

/** Base host of the backend, e.g. http://localhost:8000 — use for non-API paths like image URLs */
export const API_HOST = getApiHost();

/** Full API base URL, e.g. http://localhost:8000/api/v1 */
export const API_BASE_URL = `${API_HOST}/api/v1`;

/** How long an ordinary JSON call may take. Server think-time, not bandwidth. */
const API_TIMEOUT_MS = 30000;

/**
 * How long a FILE may take: a download, an export, a ZIP of many documents.
 *
 * These are bounded by size and the user's connection, not by how long the
 * server spends thinking, so the API timeout is the wrong unit for them
 * entirely. A scanned lease of a few dozen megabytes on a slow line needs
 * longer than any API call ever should, and it used to fail at 30s with
 * "timeout of 30000ms exceeded" — then offer a "Download instead" button that
 * made the identical request and failed identically.
 *
 * Still bounded rather than 0: a stalled transfer should eventually give up and
 * say so instead of spinning for the rest of the session.
 */
const FILE_TRANSFER_TIMEOUT_MS = 300000;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// AUDIT 2026-08-17 §5: every request and response was logged to the browser
// console in production — URLs, status codes and error detail strings, which on
// this app include document and org identifiers. Anything logged here is
// readable by anyone with the user's screen (or a support screen-share) and by
// any browser extension. Keep the tracing for development only.
const devLog = (...args: unknown[]) => {
  if (process.env.NODE_ENV !== 'production') console.log(...args);
};

// Add JWT token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // A file transfer gets the file timeout unless the caller asked for its own.
  //
  // Axios has already merged the instance default in by the time an interceptor
  // runs, so "untouched" can only be recognised as "still exactly the default" —
  // which is why that default is a named constant. A caller that genuinely wants
  // 30s on a blob should say so with a nearby value like 30001; nothing does.
  if (config.responseType === 'blob' && config.timeout === API_TIMEOUT_MS) {
    config.timeout = FILE_TRANSFER_TIMEOUT_MS;
  }
  (config as any)._startTime = Date.now();
  devLog(`[API] --> ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Silent token refresh logic
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

// Handle authentication errors with silent refresh
api.interceptors.response.use(
  (response) => {
    const duration = Date.now() - ((response.config as any)._startTime || Date.now());
    devLog(`[API] <-- ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`);
    // A WRITE that succeeds proves the read-only guard is no longer refusing
    // writes (e.g. the operator marked the invoice paid). The 402 handler only
    // ever SET this flag, so without this the banner stuck around until logout.
    const method = (response.config.method || 'get').toLowerCase();
    if (method !== 'get' && localStorage.getItem('subscription_readonly')) {
      localStorage.removeItem('subscription_readonly');
      window.dispatchEvent(new CustomEvent('subscription-readonly-cleared'));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const duration = Date.now() - ((originalRequest as any)._startTime || Date.now());
    const status = error.response?.status || 'NETWORK_ERROR';
    const detail = error.response?.data?.detail || error.message;
    devLog(`[API] <-- ${status} ${originalRequest.method?.toUpperCase()} ${originalRequest.url} (${duration}ms): ${detail}`);

    // "timeout of 30000ms exceeded" is axios talking to itself. Callers display
    // error.message straight to the user, so say what happened and what to do.
    if (error.code === 'ECONNABORTED' && /timeout/i.test(error.message || '')) {
      error.message = originalRequest.responseType === 'blob'
        ? 'This file took too long to transfer. It may be large, or the connection slow — try downloading it instead.'
        : 'The server took too long to respond. Please try again.';
    }

    // Subscription guard (402): the tenant's plan lapsed.
    //   access: "blocked"   -> log the user out
    //   access: "read_only" -> keep them in, show a read-only banner (writes are refused)
    if (error.response?.status === 402 && error.response?.data?.access) {
      const { access, subscription_state } = error.response.data;
      if (access === 'pending') {
        // Awaiting platform-admin approval — send to the wait screen (keep session).
        if (window.location.pathname !== '/pending') window.location.href = '/pending';
        return Promise.reject(error);
      }
      if (access === 'blocked') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.setItem('logout_reason', detail || 'Your subscription needs attention.');
        window.location.href = '/login?reason=subscription';
        return Promise.reject(error);
      }
      if (access === 'read_only') {
        localStorage.setItem('subscription_readonly', subscription_state || 'past_due');
        window.dispatchEvent(new CustomEvent('subscription-readonly', { detail }));
        return Promise.reject(error);
      }
    }

    // If 401 and not already a refresh/login request, attempt silent refresh
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/login')) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const token = localStorage.getItem('token');
          if (!token) throw new Error('No token');

          // Slice 2 Commit 3 — send the pb_refresh cookie alongside the
          // legacy bearer. When the cookie is present, the backend uses
          // the session path (rotation + idle-timeout); if not, it falls
          // back to bearer. withCredentials is required for the browser
          // to attach the HttpOnly cookie on a cross-origin call.
          const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          });

          const newToken = res.data.access_token;
          localStorage.setItem('token', newToken);
          localStorage.setItem('user', JSON.stringify(res.data.user));
          devLog('[API] Token refreshed successfully');

          isRefreshing = false;
          onTokenRefreshed(newToken);

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          devLog('[API] Token refresh failed — clearing session');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // Another request is already refreshing — queue this one
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }
    }

    // Non-401 or refresh failed → treat as an expired session and bounce to login.
    // BUT never do this for the login / refresh calls themselves: a 401 there means
    // "wrong credentials", not "session expired". Redirecting would reload the page
    // and swallow the error, so instead we let it propagate to the form's own
    // catch block, which shows "Invalid credentials".
    const isAuthAttempt = originalRequest.url?.includes('/auth/login')
      || originalRequest.url?.includes('/auth/refresh');
    if (error.response?.status === 401 && !isAuthAttempt) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Proactive token refresh.
//   Access tokens are 30 min (Slice 1 of auth hardening — backend
//   ACCESS_TOKEN_EXPIRE_MINUTES = 30). Check every 3 min, refresh if the
//   token has ≤ 8 min left. That gives:
//     * headroom against clock skew
//     * ~4 checks per token lifetime, so if one fails there's still a
//       later attempt before the 401 interceptor has to catch it
//     * no refresh storm on a freshly-issued token
function scheduleTokenRefresh() {
  setInterval(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // Decode JWT payload to check expiry (no verification needed client-side)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000; // convert to ms
      const now = Date.now();
      const minutesLeft = (expiresAt - now) / 60000;

      if (minutesLeft <= 8 && minutesLeft > 0) {
        devLog(`[API] Token expires in ${Math.round(minutesLeft)} min — refreshing proactively`);
        // Slice 2 Commit 3 — same withCredentials rationale as the 401
        // interceptor's refresh call: attach the pb_refresh cookie so
        // the server can use the session path (rotation + idle-timeout).
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        devLog('[API] Proactive token refresh successful');
      }
    } catch {
      // Silent failure — the 401 interceptor will handle it if the token is truly expired
    }
  }, 3 * 60 * 1000); // every 3 minutes
}
scheduleTokenRefresh();

// Export the axios instance for direct use
export { api };

// Authentication service
export const authService = {
  login: (credentials: { username: string; password: string; totp_code?: string }) =>
    api.post('/auth/login', credentials),

  register: (userData: { username: string; email: string; full_name: string; password: string; role: string }) =>
    api.post('/auth/register', userData),

  logout: () => {
    // Drop the cached plan entitlements so the next user doesn't inherit them.
    import('./entitlements').then((m) => m.clearEntitlements()).catch(() => {});
    return api.post('/auth/logout');
  },

  // AUDIT 2026-08-17 §5: several screens "logged out" by clearing localStorage
  // and navigating, without ever telling the server. The refresh cookie and the
  // session row survived, so a stolen refresh token still worked after the user
  // believed they had signed out. Every logout button must go through this.
  //
  // The server call is best-effort — a network failure must not trap the user
  // in a signed-in UI — but the local clear only happens after we have tried.
  logoutEverywhere: async () => {
    try {
      await authService.logout();
    } catch {
      // Offline or already-expired session: still clear locally.
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () =>
    api.get('/auth/me'),

  getMyPermissions: () =>
    api.get<{ role: string; permissions: string[] }>('/auth/me/permissions'),

  // createDefaultAdmin removed — the unauthenticated /auth/create-default-admin
  // bootstrap route was deleted (audit 1.3). Seed the first super_admin with a
  // server-side script, never over HTTP.

  // Requests a reset link by email. The response NEVER contains a token and is
  // identical whether or not the account exists (audit 1.2).
  requestPasswordReset: (data: { email: string }) =>
    api.post('/auth/forgot-password', data),

  resetPassword: (data: { reset_token: string; new_password: string; confirm_password: string }) =>
    api.post('/auth/reset-password', data),

  // Phase 9 — self-service profile
  getMyProfile: () =>
    api.get('/auth/me/profile'),

  updateMyProfile: (data: { phone?: string }) =>
    api.put('/auth/me/profile', data),

  changeMyPassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/me/change-password', data),

  // 2FA / TOTP (audit 1.4 #12)
  twoFAStatus: () => api.get<{ enabled: boolean }>('/auth/2fa/status'),
  getOnboarding: () => api.get<{ complete: boolean; steps: { key: string; label: string; done: boolean; link: string }[] }>('/auth/me/onboarding'),
  twoFASetup: () => api.post<{ secret: string; otpauth_uri: string }>('/auth/2fa/setup'),
  twoFAEnable: (code: string) => api.post('/auth/2fa/enable', { code }),
  twoFADisable: (code: string) => api.post('/auth/2fa/disable', { code }),

  uploadMyAvatar: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/auth/me/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  removeMyAvatar: () => api.delete('/auth/me/avatar'),
};

export const userService = {
  createUser: (userData: { username: string; email: string; password: string; role: string }) =>
    api.post<User>('/users/', userData),

  // Bulk CSV import (audit 4.6). Returns per-row results.
  bulkImport: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/users/bulk-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Per-module user seats. Each module is its own seat pool, capped by that
  // module's pack `users` quota — a user can only reach modules they're assigned.
  // tenantId is for a super_admin, who has no tenant of their own and would
  // otherwise get an empty list — which silently hides the module pickers.
  getModuleSeats: (tenantId?: number) =>
    api.get<Array<{ addon_id: number; code: string; name: string; quota: number | null; used: number }>>(
      '/users/modules/seats', { params: tenantId ? { tenant_id: tenantId } : undefined }),

  getUserModules: (userId: number) =>
    api.get<{ user_id: number; modules: string[] }>(`/users/${userId}/modules`),

  setUserModules: (userId: number, modules: string[]) =>
    api.put(`/users/${userId}/modules`, { modules }),

  getUsers: (skip = 0, limit = 100) =>
    api.get<User[]>(`/users/?skip=${skip}&limit=${limit}`),

  getAllUsers: () =>
    api.get<User[]>('/users/all'),

  exportUsers: (fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.get('/users/export', { params: { fmt }, responseType: 'blob' }),

  getUser: (userId: number) =>
    api.get<User>(`/users/${userId}`),

  updateUser: (userId: number, updateData: { is_active?: boolean; role?: string; full_name?: string; email?: string; employee_id?: string }) =>
    api.put<User>(`/users/${userId}`, updateData),

  deleteUser: (userId: number) =>
    api.delete(`/users/${userId}`),

  toggleUserStatus: (userId: number) =>
    api.patch<User>(`/users/${userId}/toggle-status`),

  // Deprecated - use authService.login instead
  login: (credentials: { username: string; password: string; totp_code?: string }) =>
    authService.login(credentials),
};

export const documentService = {
  // Auth-aware file fetch: the /documents/{id}/file endpoint is behind the
  // module gate, so an <img src>/react-pdf URL (no Authorization header) 403s.
  // Fetch it as a blob through the axios client (which attaches the token +
  // auto-refreshes) and render from an object URL.
  getFileBlob: (documentId: number) =>
    api.get(`/documents/${documentId}/file`, { responseType: 'blob' }),

  // Phase 16 — accepts both autoSubmit (existing) and priority (new) options.
  /** Checked when a file is picked: same content = duplicate (blocked),
   *  otherwise same file name = warning only. */
  uploadPrecheck: (fileName: string, fileHash: string | null, organizationId?: number) =>
    api.post<UploadPrecheckResult>('/documents/upload/precheck', {
      file_name: fileName, file_hash: fileHash, organization_id: organizationId,
    }),

  uploadDocument: (
    file: File,
    categoryId?: number,
    subcategoryId?: number,
    options?: { autoSubmit?: boolean; priority?: 'low' | 'medium' | 'high'; organizationId?: number },
  ) => {
    const formData = new FormData();
    formData.append('file', file);

    // Add category and subcategory as form data (not query params)
    if (categoryId) formData.append('category_id', categoryId.toString());
    if (subcategoryId) formData.append('subcategory_id', subcategoryId.toString());
    if (options?.autoSubmit) formData.append('auto_submit', 'true');
    // Phase 16 item 1b — Priority chosen at upload time. Backend defaults to 'medium' if omitted.
    if (options?.priority) formData.append('priority', options.priority);
    // Multi-org — which organization this document belongs to (chosen in the modal).
    if (options?.organizationId) formData.append('organization_id', options.organizationId.toString());

    return api.post<Document>('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Large files + synchronous processing can exceed the default 30s client
      // timeout — which used to abort the flow before the extra attachments
      // were sent, silently losing them.
      timeout: 180000,
    });
  },

  getDocuments: (skip = 0, limit = 100, organizationId?: number) =>
    api.get<Document[]>(`/documents/?skip=${skip}&limit=${limit}&_t=${Date.now()}${organizationId ? `&organization_id=${organizationId}` : ''}`),

  exportDocuments: (params?: { status?: string; organization_id?: number }, fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.get('/documents/export', {
      params: { ...(params?.status ? { status_filter: params.status } : {}), ...(params?.organization_id ? { organization_id: params.organization_id } : {}), fmt },
      responseType: 'blob',
    }),

  getAllDocuments: (organizationId?: number) =>
    api.get<Document[]>(`/documents/all${organizationId ? `?organization_id=${organizationId}` : ''}`),

  getDocument: (documentId: number) =>
    api.get<Document>(`/documents/${documentId}`),

  updateDocument: (documentId: number, updateData: { status?: string }) =>
    api.put<Document>(`/documents/${documentId}`, updateData),

  /** Moves it to the recycle bin (recoverable). */
  deleteDocument: (documentId: number, reason?: string) =>
    api.delete(`/documents/${documentId}`, { params: reason ? { reason } : {} }),

  listDeleted: (limit?: number, offset?: number) =>
    api.get<RecycleBinResponse>('/documents/deleted/list', { params: { limit, offset } }),

  restoreDocument: (documentId: number) =>
    api.post(`/documents/${documentId}/restore`),

  /** Erases it for good -- admins only, from the recycle bin. */
  purgeDocument: (documentId: number) =>
    api.delete<{ files_deleted: number; storage_errors: string[] }>(
      `/documents/${documentId}/permanent`, { params: { confirm: 'delete' } }),

  /** Empties the recycle bin: erases exactly these documents for good. */
  purgeAllDeleted: (documentIds: number[]) =>
    api.post<{ deleted: number; failed: { document_id: number; error: string }[]; storage_errors: number }>(
      '/documents/deleted/purge-all', { document_ids: documentIds, confirm: 'delete' }),

  getDocumentStatus: (documentId: number) =>
    api.get(`/documents/${documentId}/status`),

  processDocument: (documentId: number) =>
    api.post(`/documents/${documentId}/process`),

  resubmitDocument: (documentId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/${documentId}/resubmit`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getDocumentHistory: (documentId: number) =>
    api.get(`/documents/${documentId}/history`),

  getResubmittedDocument: (documentId: number) =>
    api.get(`/documents/${documentId}/resubmitted`),

  getDocumentCompleteJson: (documentId: number) =>
    api.get(`/documents/${documentId}/all-extracted-data`),

  validateDocumentFields: (documentId: number) =>
    api.get(`/documents/${documentId}/validate-fields`),

  updateDocumentPriority: (documentId: number, priority: string) => {
    const formData = new FormData();
    formData.append('priority', priority);
    return api.put(`/documents/${documentId}/priority`, formData);
  },

  // Attachments
  uploadAttachment: (documentId: number, file: File, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return api.post(`/documents/${documentId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },

  getAttachments: (documentId: number) =>
    api.get(`/documents/${documentId}/attachments`),

  downloadAttachment: (attachmentId: number) =>
    api.get(`/documents/attachments/${attachmentId}/download`, { responseType: 'blob' }),

  deleteAttachment: (attachmentId: number) =>
    api.delete(`/documents/attachments/${attachmentId}`),

  // Remarks
  addRemark: (documentId: number, remarks: string) => {
    const formData = new FormData();
    formData.append('remarks', remarks);
    return api.put(`/documents/${documentId}/remarks`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getRemarks: (documentId: number) =>
    api.get(`/documents/${documentId}/remarks`),

  // Zoho push retry flow on failed docs.
  //   reopenZohoCorrection(id, reason) opens a single-use correction
  //     window. While open, fields are editable by the reopener even
  //     on archived docs. Server enforces min 5 / max 2000 chars.
  //   retryZohoPush(id) closes the window and re-enqueues the push
  //     job. Server's BackgroundTask runs it almost immediately;
  //     the 60s safety net catches it if the BackgroundTask fails.
  reopenZohoCorrection: (documentId: number, reason: string) =>
    api.post<{
      document_id: number;
      zoho_reopened_at: string;
      zoho_reopened_by_user_id: number;
      reason: string;
      message: string;
    }>(`/documents/${documentId}/zoho/reopen`, { reason }),

  retryZohoPush: (documentId: number) =>
    api.post<{
      document_id: number;
      push_job_id: number;
      message: string;
    }>(`/documents/${documentId}/zoho/retry`),

};


export const fieldDefinitionService = {
  createFieldDefinition: (fieldData: {
    field_name: string;
    field_type: string;
    is_required?: boolean;
    display_label?: string;
    is_visible?: boolean;
    display_order?: number;
  }) =>
    api.post<FieldDefinition>('/fields/', fieldData),

  getFieldDefinitions: (skip = 0, limit = 100) =>
    api.get<FieldDefinition[]>(`/fields/?skip=${skip}&limit=${limit}`),

  getHierarchicalFieldDefinitions: () =>
    api.get<FieldDefinition[]>('/fields/hierarchical'),

  getVisibleFieldDefinitions: () =>
    api.get<FieldDefinition[]>('/fields/visible'),

  getFieldDefinition: (fieldId: number) =>
    api.get<FieldDefinition>(`/fields/${fieldId}`),

  updateFieldDefinition: (fieldId: number, updateData: {
    display_label?: string;
    is_visible?: boolean;
    display_order?: number;
    is_required?: boolean;
  }) =>
    api.put<FieldDefinition>(`/fields/${fieldId}`, updateData),

  toggleFieldVisibility: (fieldId: number) =>
    api.patch<FieldDefinition>(`/fields/${fieldId}/visibility`),

  deleteFieldDefinition: (fieldId: number) =>
    api.delete(`/fields/${fieldId}`),

  createTableFieldWithColumns: (tableData: {
    field_name: string;
    display_label?: string;
    is_required?: boolean;
    display_order?: number;
    line_items: Array<{
      field_name: string;
      display_label?: string;
      field_type: string;
      is_required?: boolean;
      display_order?: number;
    }>;
  }) =>
    api.post<FieldDefinition>('/fields/table', tableData),

  // Table column management
  addColumnToTable: (tableFieldId: number, columnFieldId: number, displayOrder?: number) =>
    api.post(`/fields/${tableFieldId}/columns/${columnFieldId}`, null, {
      params: { display_order: displayOrder }
    }),

  removeColumnFromTable: (tableFieldId: number, columnFieldId: number) =>
    api.delete(`/fields/${tableFieldId}/columns/${columnFieldId}`),

  getFieldUsage: (fieldId: number) =>
    api.get<{ field_id: number; field_name: string; used_in_tables: Array<{ table_field_id: number; table_field_name: string }>; usage_count: number }>(`/fields/${fieldId}/usage`),

  reorderTableColumns: (tableFieldId: number, columnOrders: Array<{column_field_id: number, display_order: number}>) =>
    api.put(`/fields/${tableFieldId}/columns/reorder`, columnOrders),

  // Alias methods for compatibility
  getAllFields: () =>
    api.get<FieldDefinition[]>('/fields/hierarchical'),

  createField: (fieldData: {
    field_name: string;
    field_type: string;
    field_category: string;
    is_required?: boolean;
    display_order?: number;
    display_label?: string;
    is_visible?: boolean;
    list_options?: string[];
  }) =>
    api.post<FieldDefinition>('/fields/', fieldData),

  updateField: (fieldId: number, updateData: {
    field_name?: string;
    field_type?: string;
    field_category?: string;
    is_required?: boolean;
    display_order?: number;
    display_label?: string;
    is_visible?: boolean;
    list_options?: string[];
  }) =>
    api.put<FieldDefinition>(`/fields/${fieldId}`, updateData),

  deleteField: (fieldId: number) =>
    api.delete(`/fields/${fieldId}`),
};

export const documentFieldService = {
  createDocumentField: (fieldData: { document_id: number; field_id: number; field_value?: string; extracted_confidence?: number }) =>
    api.post<DocumentField>('/document-fields/', fieldData),

  getDocumentFields: (skip = 0, limit = 100) =>
    api.get<DocumentField[]>(`/document-fields/?skip=${skip}&limit=${limit}`),

  getDocumentFieldsByDocument: (documentId: number) =>
    api.get<DocumentField[]>(`/document-fields/document/${documentId}`),

  getDocumentField: (docFieldId: number) =>
    api.get<DocumentField>(`/document-fields/${docFieldId}`),

  updateDocumentField: (docFieldId: number, updateData: { field_value?: string; is_verified?: boolean }) =>
    api.put<DocumentField>(`/document-fields/${docFieldId}`, updateData),

  deleteDocumentField: (docFieldId: number) =>
    api.delete(`/document-fields/${docFieldId}`),
};

export const auditLogService = {
  createAuditLog: (auditData: { document_id: number; action: string; comments?: string }) =>
    api.post<AuditLog>('/audits/', auditData),

  getAuditLogs: (skip = 0, limit = 100) =>
    api.get<AuditLog[]>(`/audits/?skip=${skip}&limit=${limit}`),

  getAuditLogsByDocument: (documentId: number) =>
    api.get<AuditLog[]>(`/audits/document/${documentId}`),

  getAuditLog: (auditId: number) =>
    api.get<AuditLog>(`/audits/${auditId}`),
};

export const notificationService = {
  createNotification: (notificationData: { user_id: number; document_id: number; message: string }) =>
    api.post<Notification>('/notifications/', notificationData),

  getNotifications: (skip = 0, limit = 100) =>
    api.get<Notification[]>(`/notifications/?skip=${skip}&limit=${limit}`),

  getNotificationsByUser: (userId: number) =>
    api.get<Notification[]>(`/notifications/user/${userId}`),

  getNotification: (notificationId: number) =>
    api.get<Notification>(`/notifications/${notificationId}`),

  markNotificationAsRead: (notificationId: number) =>
    api.put<Notification>(`/notifications/${notificationId}/read`),

  /** One request marks every unread notification of the current user as read. */
  markAllNotificationsRead: () =>
    api.put<{ updated: number }>('/notifications/read-all'),
};

export const organizationService = {
  getAllOrganizations: (includeInactive?: boolean) =>
    api.get('/organizations/', { params: includeInactive ? { include_inactive: true } : {} }),

  // Orgs the current user may create records in (works for non-admin multi-org users too).
  getAccessibleOrganizations: () =>
    api.get('/organizations/accessible'),

  getMyOrganization: () =>
    api.get('/organizations/my-organization'),

  updateMyOrganization: (updateData: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    gstin?: string;
    pan?: string;
    gst_return_frequency?: string;
    legal_status?: string;
    pf_esi_applicability?: string;
    sebi_listing_applicability?: string;
    escalation_emails?: string[];
  }) =>
    api.put('/organizations/my-organization', updateData),

  // Tenant admin: create an additional organization within their tenant (no new admin user).
  createOrganization: (data: {
    organization_name: string;
    tenant_id?: number;          // super admin only: which tenant it belongs to
    email: string;
    phone: string;
    address: string;
    gstin?: string;
    pan?: string;
    gst_return_frequency?: string;
    legal_status?: string;
    pf_esi_applicability?: string;
    sebi_listing_applicability?: string;
    escalation_emails?: string[];
  }) =>
    api.post('/organizations/register', data),

  // Tenant admin / super admin: update ANY org they administer (incl. is_active).
  updateOrganizationById: (organizationId: number, updateData: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    gstin?: string;
    pan?: string;
    gst_return_frequency?: string;
    legal_status?: string;
    pf_esi_applicability?: string;
    sebi_listing_applicability?: string;
    escalation_emails?: string[];
    is_active?: boolean;
  }) =>
    api.put(`/organizations/${organizationId}`, updateData),

  getOrganizationUsers: () =>
    api.get('/organizations/my-organization/users'),

  updateUserRole: (userId: number, role: string) =>
    api.put(`/organizations/my-organization/users/${userId}/role`, { role }),

  deactivateUser: (userId: number) =>
    api.delete(`/organizations/my-organization/users/${userId}`),
};

export const categoryService = {
  getAllCategories: () =>
    api.get<Category[]>('/categories/'),

  getCategory: (categoryId: number) =>
    api.get<Category>(`/categories/${categoryId}`),

  getSubcategories: (categoryId: number) =>
    api.get<Category[]>(`/categories/${categoryId}/subcategories`),

  updateCategory: (categoryId: number, data: { enable_ocr_extraction?: boolean; display_order?: number }) =>
    api.put(`/categories/${categoryId}`, data),
};

// ===== Tally integration (agent-based). Backend at /api/v1/tally =====
export interface TallyExtraField {
  key: string;
  label: string;
  placeholder?: string;
}
export interface TallyStatus {
  connected: boolean;
  status: string;                 // not_connected | pending | active | inactive
  tally_company?: string | null;
  agent_online?: boolean;
  agent_version?: string | null;
  tally_version?: string | null;
  agent_last_seen_at?: string | null;
  last_masters_sync_at?: string | null;
  last_error?: string | null;
  auto_push_on_approval?: boolean;
  require_ledger_review?: boolean;
  park_in_tally?: boolean;               // push Optional (parked) + enrich in Tally
  manual_voucher_numbering?: boolean;    // put the document's invoice no on the voucher
  sales_voucher_mode?: string;           // item_invoice | accounting_invoice | as_voucher
  enrichment_extra_fields?: string[];    // common extra-field keys collected on review
  enrichment_field_catalog?: TallyExtraField[];  // which keys exist + their labels
  push_category_ids?: number[];
  // Editable field-mapping targets (resolved: override or built-in default).
  sales_ledger_name?: string;
  party_ledger_group?: string;
  tax_ledger_group?: string;
}
export interface TallyLedger {
  name: string;
  guid?: string | null;
  parent?: string | null;
  group?: string;
  gstin?: string | null;
  state?: string | null;
}
export interface TallyFieldMapping {
  source: string;
  target: string;
  note?: string;
}
export interface TallyMappings {
  field_mappings: TallyFieldMapping[];
  defaults: { sales_ledger_name: string; party_ledger_group: string; tax_ledger_group: string };
  sales_ledger_name: string;
  party_ledger_group: string;
  tax_ledger_group: string;
  synced_ledger_count: number;
  last_masters_sync_at?: string | null;
}
export interface TallyConnectResponse {
  organization_id: number;
  agent_id?: number;
  enrollment_token: string;
  expires_at: string;
  status: string;
}
// Multi-org: an org the admin can configure Tally for (org switcher).
export interface TallyOrgSummary {
  organization_id: number;
  name: string;
  connected: boolean;
  tally_company?: string | null;
  agent_id?: number | null;
  agent_online: boolean;
}
// A Tally agent (one Tally install) in the tenant.
export interface TallyAgentSummary {
  agent_id: number;
  name?: string | null;
  status: string;
  enrolled: boolean;
  agent_online: boolean;
  served_org_ids: number[];
  companies: string[];
}
export interface TallyJob {
  id: number;
  voucher_type: string;
  status: string;
  attempts: number;
  tally_voucher_id?: string | null;
  tally_voucher_number?: string | null;
  tally_master_id?: string | null;
  sales_ledger_name?: string | null;
  review_status?: string | null;
  is_optional?: boolean;
  enrichment_status?: string | null;    // parked | enriched | gone
  document_id?: number | null;
  request_payload?: {
    voucher_number?: string;
    party_ledger?: string;
    amount?: number;
    [k: string]: unknown;
  } | null;
  last_error?: string | null;
  created_at: string;
  finished_at?: string | null;
}

// Append ?organization_id / &organization_id for the selected org (multi-org).
// Omitted → backend defaults to the caller's primary org (single-org unchanged).
const orgQ = (orgId?: number, sep: '?' | '&' = '?') => (orgId != null ? `${sep}organization_id=${orgId}` : '');

export const tallyService = {
  // Multi-org config
  listOrgs: () => api.get<{ orgs: TallyOrgSummary[] }>('/tally/orgs'),
  listAgents: () => api.get<{ agents: TallyAgentSummary[] }>('/tally/agents'),
  linkAgent: (organization_id: number, agent_id: number) =>
    api.post<{ organization_id: number; agent_id: number; status: string; linked: boolean }>('/tally/link-agent', { organization_id, agent_id }),

  getStatus: (orgId?: number) => api.get<TallyStatus>(`/tally/status${orgQ(orgId)}`),
  connect: (orgId?: number) => api.post<TallyConnectResponse>(`/tally/connect${orgQ(orgId)}`),
  disconnect: (orgId?: number) => api.post<{ disconnected?: boolean; message?: string }>(`/tally/disconnect${orgQ(orgId)}`),
  getMappings: (orgId?: number) => api.get<TallyMappings>(`/tally/mappings${orgQ(orgId)}`),
  updateSettings: (data: {
    auto_push_on_approval?: boolean;
    require_ledger_review?: boolean;
    park_in_tally?: boolean;
    manual_voucher_numbering?: boolean;
    sales_voucher_mode?: string;
    enrichment_extra_fields?: string[];
    push_category_ids?: number[];
    tally_company?: string;
    sales_ledger_name?: string;
    party_ledger_group?: string;
    tax_ledger_group?: string;
  }, orgId?: number) =>
    api.post<{ auto_push_on_approval: boolean; push_category_ids: number[] }>(`/tally/settings${orgQ(orgId)}`, data),
  listJobs: (orgId?: number) => api.get<TallyJob[]>(`/tally/jobs${orgQ(orgId)}`),
  getLedgers: (group: 'all' | 'sales' | 'party' | 'tax' = 'all', orgId?: number) =>
    api.get<{ group: string; ledgers: TallyLedger[]; as_of?: string | null }>(`/tally/ledgers?group=${group}${orgQ(orgId, '&')}`),
  releaseJob: (jobId: number, sales_ledger_name: string, orgId?: number, extras?: Record<string, string>) =>
    api.post<{ id: number; status: string; sales_ledger_name: string }>(`/tally/jobs/${jobId}/release${orgQ(orgId)}`, { sales_ledger_name, extras }),
  rejectJob: (jobId: number, orgId?: number) => api.post<{ id: number; status: string }>(`/tally/jobs/${jobId}/reject${orgQ(orgId)}`),
  voucherPdf: (jobId: number, orgId?: number) => api.get(`/tally/jobs/${jobId}/voucher.pdf${orgQ(orgId)}`, { responseType: 'blob' }),

  // Re-file a document under a different sub-category. Offered from the
  // Tally panel because that is where a wrong filing surfaces.
  changeDocumentSubcategory: (docId: number, subcategoryId: number) =>
    api.put<{ subcategory_id: number; subcategory_name: string; note?: string;
      workflows_cancelled?: number[]; workflows_started?: number[] }>(
      `/tally/documents/${docId}/subcategory`, { subcategory_id: subcategoryId }),
  // Document-scoped push status + resubmit (for approvers on the document view).
  documentPushStatus: (docId: number) => api.get<{
    has_job: boolean; job_id?: number; status?: string; sales_ledger_name?: string | null;
    voucher_number?: string | null; last_error?: string | null; finished_at?: string | null;
    is_optional?: boolean; enrichment_status?: string | null; can_resubmit?: boolean;
    can_push?: boolean; tally_configured?: boolean; default_sales_ledger?: string | null; needs_ledger?: boolean;
    // Format the latest job used, else the org default — seeds the picker.
    sales_voucher_mode?: string;
    // Set when the push cannot run whatever the user does, with the
    // sub-categories the document could be re-filed under.
    push_blocked_reason?: string | null;
    subcategory_id?: number | null;
    subcategory_options?: Array<{ id: number; name: string }>;
    // Names corrected to the master's spelling before posting.
    auto_corrections?: Array<{ field: string; from: string; to: string; why: string }>;
  }>(`/tally/documents/${docId}/push-status`),
  // salesVoucherMode overrides the org's configured Tally format for this push
  // only (the document page offers the three next to the button).
  resubmitDocumentPush: (docId: number, salesVoucherMode?: string) =>
    api.post<{ job_id: number; status: string }>(
      `/tally/documents/${docId}/resubmit`, { sales_voucher_mode: salesVoucherMode || null }),
  // Manual push from the document page (final approver / admin). Optional ledger
  // triggers a one-click map & release when the org holds jobs for review.
  pushDocument: (docId: number, salesLedgerName?: string, salesVoucherMode?: string) =>
    api.post<{ job_id: number; status: string; sales_ledger_name?: string | null; needs_ledger?: boolean }>(
      `/tally/documents/${docId}/push`,
      { sales_ledger_name: salesLedgerName || null, sales_voucher_mode: salesVoucherMode || null }),
  releaseDocumentPush: (docId: number, salesLedgerName: string) =>
    api.post<{ job_id: number; status: string; sales_ledger_name?: string | null }>(
      `/tally/documents/${docId}/release`, { sales_ledger_name: salesLedgerName }),
};

export const categoryFieldDefinitionService = {
  createCategoryFieldDefinition: (fieldData: {
    field_id: number;
    category_id: number;
    subcategory_id?: number;
    field_type: string;
    is_required?: boolean;
    is_visible?: boolean;
    display_order?: number;
    display_label?: string;
  }) =>
    api.post<CategoryFieldDefinition>('/category-fields/', fieldData),

  createBulkCategoryFieldDefinitions: (bulkData: {
    category_id: number;
    subcategory_id?: number;
    fields: Array<{
      field_id: number;
      field_type: string;
      is_required?: boolean;
      is_visible?: boolean;
      display_order?: number;
      display_label?: string;
    }>;
  }) =>
    api.post<CategoryFieldDefinition[]>('/category-fields/bulk', bulkData),

  getFieldsForCategory: (categoryId: number, subcategoryId?: number) =>
    api.get<{
      category_id: number;
      category_name: string;
      subcategory_id?: number;
      subcategory_name?: string;
      fields: CategoryFieldDefinition[];
    }>(`/category-fields/category/${categoryId}${subcategoryId ? `?subcategory_id=${subcategoryId}` : ''}`),

  getAllCategoryFieldDefinitions: (categoryId?: number, subcategoryId?: number) => {
    let url = '/category-fields/';
    const params = new URLSearchParams();
    if (categoryId) params.append('category_id', categoryId.toString());
    if (subcategoryId) params.append('subcategory_id', subcategoryId.toString());
    if (params.toString()) url += `?${params.toString()}`;
    return api.get<CategoryFieldDefinition[]>(url);
  },

  getCategoryFieldDefinition: (definitionId: number) =>
    api.get<CategoryFieldDefinition>(`/category-fields/${definitionId}`),

  updateCategoryFieldDefinition: (definitionId: number, updateData: {
    field_type?: string;
    is_required?: boolean;
    is_visible?: boolean;
    display_order?: number;
    display_label?: string;
  }) =>
    api.put<CategoryFieldDefinition>(`/category-fields/${definitionId}`, updateData),

  deleteCategoryFieldDefinition: (definitionId: number) =>
    api.delete(`/category-fields/${definitionId}`),

  deleteAllFieldsForCategory: (categoryId: number, subcategoryId?: number) =>
    api.delete(`/category-fields/category/${categoryId}/all${subcategoryId ? `?subcategory_id=${subcategoryId}` : ''}`),
};

// Super Admin Service (legacy — use tenantService for new tenant management)
export const superAdminService = {
  // Get all organizations with statistics
  getAllOrganizationStats: () =>
    api.get('/organizations/super-admin/stats'),

  // Create a new organization within a tenant
  createOrganization: (orgData: {
    name: string;
    tenant_id?: number;
    email: string;
    phone: string;
    address: string;
    gstin?: string;
    pan?: string;
    gst_return_frequency?: string;
    escalation_emails?: string[];
    admin_username?: string;
    admin_email?: string;
    admin_full_name?: string;
    admin_password?: string;
  }) =>
    api.post('/organizations/register', orgData),

  // Get users for any organization
  getOrganizationUsers: (organizationId: number) =>
    api.get(`/organizations/super-admin/${organizationId}/users`),

  // Update any organization's details (super admin only)
  updateOrganization: (organizationId: number, updateData: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    gstin?: string;
    pan?: string;
    gst_return_frequency?: string;
    legal_status?: string;
    pf_esi_applicability?: string;
    sebi_listing_applicability?: string;
    escalation_emails?: string[];
  }) =>
    api.put(`/organizations/super-admin/${organizationId}`, updateData),

  // Toggle organization active status
  toggleOrganizationActive: (organizationId: number) =>
    api.put(`/organizations/super-admin/${organizationId}/toggle-active`),

  // Get all organizations
  getAllOrganizations: () =>
    api.get('/organizations/'),
};

// Tenant Service (super_admin only)
export const tenantService = {
  list: () =>
    api.get('/tenants/'),

  get: (tenantId: number) =>
    api.get(`/tenants/${tenantId}`),

  create: (data: {
    name: string;
    slug: string;
    email?: string;
    phone?: string;
    admin_username: string;
    admin_email: string;
    admin_full_name: string;
    admin_password: string;
  }) =>
    api.post('/tenants/', data),

  update: (tenantId: number, data: {
    name?: string;
    slug?: string;
    email?: string;
    phone?: string;
    is_active?: boolean;
  }) =>
    api.put(`/tenants/${tenantId}`, data),

  toggleActive: (tenantId: number) =>
    api.put(`/tenants/${tenantId}/toggle-active`),
};

// Department Service
export const departmentService = {
  list: (organizationId?: number) =>
    api.get('/departments/', { params: organizationId ? { organization_id: organizationId } : undefined }),

  get: (departmentId: number) =>
    api.get(`/departments/${departmentId}`),

  // primary_user_id = the department head (documents go to them first);
  // secondary_user_id takes over when the primary can't. Both optional.
  create: (data: { name: string; organization_id: number; department_type?: string | null;
                   primary_user_id?: number | null; secondary_user_id?: number | null }) =>
    api.post('/departments/', data),

  update: (departmentId: number, data: { name?: string; is_active?: boolean; department_type?: string | null;
                                         primary_user_id?: number | null; secondary_user_id?: number | null }) =>
    api.put(`/departments/${departmentId}`, data),
};

// WhatsApp senders — platform (super-admin) mapping of a business number to a
// tenant + routing mode + Meta Phone Number ID. Not tenant-facing.
export const whatsappSenderService = {
  list: () => api.get('/whatsapp/senders/'),

  upsert: (data: {
    business_number: string;
    organization_id: number;
    routing_mode: string;
    phone_number_id?: string;
    label?: string;
  }) => api.post('/whatsapp/senders/', data),

  update: (senderId: number, data: {
    organization_id?: number;
    routing_mode?: string;
    phone_number_id?: string;
    label?: string;
    is_active?: boolean;
  }) => api.patch(`/whatsapp/senders/${senderId}`, data),
};

// Roles & permissions — read-only from the main app's perspective.
// The Site Panel owns the catalogue (POST /role-permissions/create etc.);
// the main app just needs to enumerate roles for assignment dropdowns.
export const rolePermissionService = {
  list: () => api.get('/role-permissions/'),
};

// Recipe layer observability — CPU-vs-GPU savings + per-recipe health.
// Tenant-scoped and admin-only on the backend (see app/api/recipes.py).
// Super admin: new self-serve workspaces waiting for approval.
export interface PendingSignup {
  tenant_id: number; tenant_name: string | null; admin_name: string | null; admin_email: string | null;
  org_name: string | null; org_city: string | null; org_state: string | null; gstin: string | null;
  created_at: string | null;
}
export const platformSignupService = {
  list: () => api.get<PendingSignup[]>('/platform/signups'),
  approve: (tenantId: number) => api.post(`/platform/tenants/${tenantId}/approve-signup`),
  reject: (tenantId: number) => api.post(`/platform/tenants/${tenantId}/reject-signup`),
};

// Super admin: plan changes, seats and add-ons that tenants asked for.
export interface PlatformAddonRequest extends AddonRequest {
  tenant_id: number; tenant_name: string | null; effective_total: number | null; invoice_number: string | null;
}
export interface PlatformPlanRequest {
  id: number; tenant_id: number; tenant_name: string | null; kind: string;
  module: { addon_id: number; code: string; name: string } | null;
  requested_pack: { name?: string } | null;
  requested_page_plan: { name?: string } | null;
  requested_user_count: number | null;
  note: string | null; status: 'pending' | 'approved' | 'rejected';
  requested_by_name: string | null; scheduled_for: string | null; created_at: string;
}
export const platformRequestService = {
  addons: () => api.get<PlatformAddonRequest[]>('/platform/addon-requests', { params: { status: 'pending' } }),
  plans: () => api.get<PlatformPlanRequest[]>('/platform/module-pack-requests', { params: { status: 'pending' } }),
  approveAddon: (id: number) => api.post(`/platform/addon-requests/${id}/approve`, {}),
  rejectAddon: (id: number) => api.post(`/platform/addon-requests/${id}/reject`, {}),
  approvePlan: (id: number) => api.post(`/platform/module-pack-requests/${id}/approve`, {}),
  rejectPlan: (id: number) => api.post(`/platform/module-pack-requests/${id}/reject`, {}),
};

export const recipeService = {
  getStats: () => api.get('/recipes/stats'),
};

// Operator-level platform settings (super-admin). Includes the runtime
// processing toggles surfaced on the Platform Settings page.
export interface PlatformSettings {
  dedup_uploads_enabled: boolean;
  recipe_layer_enabled: boolean;
  [key: string]: unknown;
}
export const platformSettingsService = {
  get: () => api.get<PlatformSettings>('/platform/settings'),
  patch: (data: Partial<PlatformSettings>) => api.patch<PlatformSettings>('/platform/settings', data),
};

// WhatsApp vendor allowlist (tenant-admin, org-scoped — the backend derives the
// org from the token, the client never sends it). Ported from dev 2026-08-22.
export const whatsappVendorService = {
  // Channel: which bot the tenant's vendors use — the shared PaperBridge bot
  // (default) or the tenant's own onboarded number.
  getChannel: () => api.get('/whatsapp/vendors/channel'),
  setOwnNumber: (data: { business_number: string; phone_number_id: string; label?: string }) =>
    api.put('/whatsapp/vendors/channel', data),
  useSharedBot: () => api.delete('/whatsapp/vendors/channel'),

  list: () => api.get('/whatsapp/vendors/'),

  add: (data: { phone_number: string; display_name?: string }) =>
    api.post('/whatsapp/vendors/', data),

  remove: (vendorId: number) =>
    api.delete(`/whatsapp/vendors/${vendorId}`),

  importCsv: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/whatsapp/vendors/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// SLA service
export const slaService = {
  // Workspace holidays: business-day SLA clocks skip them like weekends.
  listHolidays: () => api.get<{ id: number; date: string; name: string | null }[]>('/sla/holidays'),
  addHoliday: (date: string, name?: string) => api.post('/sla/holidays', { date, name }),
  deleteHoliday: (id: number) => api.delete(`/sla/holidays/${id}`),

  getConfig: (organizationId: number) =>
    api.get(`/sla/config/${organizationId}`),

  updateConfig: (organizationId: number, configs: Array<{
    priority: string;
    stage: string;
    sla_days: number;
    use_business_days: boolean;
    auto_approve_after_breaches: number;
    breach_notification_interval_hours: number;
    breach_reminder_offsets: string;
    auto_escalate_after_days: number;
  }>) =>
    api.put(`/sla/config/${organizationId}`, { configs }),

  getDashboard: (params?: { organization_id?: number; priority?: string; stage?: string; user_id?: number; year?: number; month?: number }) =>
    api.get('/sla/dashboard', { params }),

  getBreached: (params?: { organization_id?: number; priority?: string; user_id?: number; year?: number; month?: number }) =>
    api.get('/sla/breached', { params }),

  getDocumentSla: (documentId: number) =>
    api.get(`/sla/document/${documentId}`),

  getActorStats: (params?: { organization_id?: number }) =>
    api.get('/sla/actor-stats', { params }),

  getTeamPerformance: (params?: { organization_id?: number; user_id?: number; year?: number; month?: number }) =>
    api.get('/sla/team-performance', { params }),

  getMyStats: () =>
    api.get('/sla/my-stats'),

  getLockStatus: (organizationId: number) =>
    api.get(`/sla/config/${organizationId}/lock-status`),

  unlockConfig: (organizationId: number) =>
    api.post(`/sla/config/${organizationId}/unlock`),
};

// Workflow service
export const workflowService = {
  getAssignableUsers: (documentId: number) =>
    api.get(`/workflow/documents/${documentId}/assignable-users`),
  assignDocument: (documentId: number, userId: number) =>
    api.post(`/workflow/documents/${documentId}/assign`, { user_id: userId }),
};

export interface TaskAttachment {
  attachment_id: number; file_name: string; file_type?: string | null; file_size?: number | null;
  uploaded_by_user_id?: number | null; uploaded_by_name?: string | null; uploaded_at?: string | null;
  uploaded_by_party?: boolean;
  // 'task' = a file on the task; 'comment' = attached to a comment (one Files list shows both).
  kind?: 'task' | 'comment';
  // On an internal note of a shared task: the vendor does not see it.
  internal?: boolean;
  can_edit?: boolean;
}

export const taskService = {
  createTask: (data: any) => api.post('/tasks/', data),
  getTasks: (params?: any) => api.get('/tasks/', { params }),
  getTaskCounts: (organizationId?: number) => api.get('/tasks/counts', { params: organizationId ? { organization_id: organizationId } : undefined }),
  getTask: (taskId: number) => api.get(`/tasks/${taskId}`),
  updateTask: (taskId: number, data: any) => api.put(`/tasks/${taskId}`, data),
  assignTask: (taskId: number, userId: number) => api.put(`/tasks/${taskId}/assign`, { assignee_user_id: userId }),
  // Close (no longer needed) and Reopen both need a reason; it is e-mailed to the assignee.
  closeTask: (taskId: number, reason: string) => api.post(`/tasks/${taskId}/close`, { reason }),
  reopenTask: (taskId: number, reason: string) => api.post(`/tasks/${taskId}/reopen`, { reason }),
  // Someone outside, by e-mail alone: a one-use magic link to reply / upload.
  checkGuestEmail: (email: string) => api.get<{ ok: boolean; email: string; message: string }>('/tasks/check-email', { params: { email } }),
  assignToGuest: (taskId: number, email: string) => api.post(`/tasks/${taskId}/guest`, { email }),
  resendGuestLink: (taskId: number) => api.post(`/tasks/${taskId}/guest/resend`),
  // visibility: 'internal' (staff only, default) or 'party' (the shared vendor / customer sees it).
  addComment: (taskId: number, content: string, visibility: 'internal' | 'party' = 'internal') =>
    api.post(`/tasks/${taskId}/comments`, { content, visibility }),
  // One comment on several tasks / sub-tasks; one e-mail per person.
  bulkComment: (taskIds: number[], content: string, visibility: 'internal' | 'party' = 'internal') =>
    api.post('/tasks/bulk-comment', { task_ids: taskIds, content, visibility }),
  // Share with a vendor / customer by their portal e-mail (look up first to confirm the name).
  lookupParty: (taskId: number, email: string) =>
    api.get<{ found: boolean; message?: string; party_id?: number; name?: string; party_type?: string }>(
      `/tasks/${taskId}/party-lookup`, { params: { email } }),
  // The same lookup before the task exists (Create Task), in the chosen organization.
  lookupPartyInOrg: (email: string, organizationId?: number) =>
    api.get<{ found: boolean; message?: string; party_id?: number; name?: string; party_type?: string }>(
      '/tasks/party-lookup', { params: { email, organization_id: organizationId } }),
  shareTask: (taskId: number, email: string) => api.put(`/tasks/${taskId}/share`, { email }),
  unshareTask: (taskId: number) => api.delete(`/tasks/${taskId}/share`),
  getComments: (taskId: number) => api.get(`/tasks/${taskId}/comments`),
  // A comment with files (Word, Excel, PDF, images). Text is optional when a file is attached.
  addCommentWithFiles: (taskId: number, content: string, files: File[], visibility: 'internal' | 'party' = 'internal') => {
    const fd = new FormData();
    fd.append('content', content);
    fd.append('visibility', visibility);
    files.forEach(f => fd.append('files', f));
    return api.post(`/tasks/${taskId}/comments/with-files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadCommentAttachment: (attachmentId: number) =>
    api.get(`/tasks/comment-attachments/${attachmentId}/download`, { responseType: 'blob' }),
  // Files on the task itself: any number, each with View / Edit (rename, replace, remove).
  getTaskAttachments: (taskId: number) =>
    api.get<{ attachments: TaskAttachment[]; can_add: boolean }>(`/tasks/${taskId}/attachments`),
  // quiet: no "new file" e-mail (the Create Task form's reference files -- the
  // new-task e-mail already covers them).
  addTaskAttachments: (taskId: number, files: File[], quiet = false) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return api.post<{ attachments: TaskAttachment[]; can_add: boolean }>(`/tasks/${taskId}/attachments`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' }, params: quiet ? { quiet: true } : undefined });
  },
  renameTaskAttachment: (attachmentId: number, fileName: string) =>
    api.patch<TaskAttachment>(`/tasks/attachments/${attachmentId}`, { file_name: fileName }),
  replaceTaskAttachment: (attachmentId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.put<TaskAttachment>(`/tasks/attachments/${attachmentId}/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteTaskAttachment: (attachmentId: number) => api.delete(`/tasks/attachments/${attachmentId}`),
  downloadTaskAttachment: (attachmentId: number) =>
    api.get(`/tasks/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  viewTaskAttachment: (attachmentId: number) =>
    api.get(`/tasks/attachments/${attachmentId}/download`, { params: { inline: true }, responseType: 'blob' }),
  getAssignableUsers: (organizationId?: number) =>
    api.get('/tasks/assignable-users', { params: organizationId ? { organization_id: organizationId } : undefined }),
  // Attach an image to a task (multipart). Returns { image_url }.
  exportTasksXlsx: (rows: any[], fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.post('/tasks/export', rows, { params: { fmt }, responseType: 'blob', timeout: 60000 }),

  uploadTaskImage: (taskId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/tasks/${taskId}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Phase 15 item 5b — sub-tasks
  getSubtasks: (taskId: number) => api.get(`/tasks/${taskId}/subtasks`),
  // Bulk reassignment of one user's active tasks to another (admin only)
  getReassignCandidates: (fromUserId: number, status?: string) =>
    api.get('/tasks/reassign-candidates', { params: { from_user_id: fromUserId, ...(status ? { status } : {}) } }),
  reassignTasksBulk: (data: { from_user_id: number; to_user_id: number; task_ids?: number[] }) =>
    api.post('/tasks/reassign-bulk', data),
  // Task <-> Document linkage (any module: document|property|agreement|license|insurance)
  linkDocument: (taskId: number, data: { source_type: string; record_id: number }) =>
    api.post(`/tasks/${taskId}/documents`, data),
  unlinkDocument: (taskId: number, linkId: number) =>
    api.delete(`/tasks/${taskId}/documents/${linkId}`),
  listTaskDocuments: (taskId: number) => api.get(`/tasks/${taskId}/documents`),
  tasksForDocument: (source_type: string, record_id: number) =>
    api.get('/tasks/for-document', { params: { source_type, record_id } }),
  documentLinkCounts: (source_type: string, record_ids: number[]) =>
    api.get('/tasks/document-link-counts', { params: { source_type, record_ids: record_ids.join(',') } }),
};

// ─── Timesheets ──────────────────────────────────────────────────────────────
// Working Time -> Task Time -> Work Output -> Compliance.
// Every derived figure (durations, totals, avg/doc) comes from the SERVER — the
// editor's live preview is a convenience and is always replaced by the response.
export interface TimesheetTaskType {
  task_type_id: number;
  name: string;
  /** Unit the count is expressed in — "docs", "returns", "others", … */
  uom: string;
  description?: string | null;
  is_active: boolean;
  sort_order: number;
  /** The department whose members may log this type. Mandatory on new types;
   *  null only on types that predate the field and still need assigning. */
  department_id?: number | null;
  /** Resolved for display; null means unassigned, not "shared". */
  department_name?: string | null;
}

export interface TimesheetEntryRow {
  entry_id?: number | null;
  task_number?: string | null;      // serial no, typed by the employee
  description?: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
  task_type_id?: number | null;
  task_type_name?: string | null;
  /** Snapshotted from the task type at save time. */
  uom?: string | null;
  start_time?: string | null;       // "HH:MM"
  end_time?: string | null;
  duration_minutes?: number;
  duration_label?: string;
  unit_count?: number | null;
  /** null when it cannot be computed — render "N/A", never 0. */
  avg_minutes_per_unit?: number | null;
  units_per_hour?: number | null;
}

export interface TimesheetException {
  exception_id: number;
  timesheet_id: number;
  entry_id?: number | null;
  user_id: number;
  user_name?: string | null;
  work_date: string;
  code: string;
  severity: 'error' | 'warning';
  message: string;
  status: 'open' | 'acknowledged' | 'resolved';
  resolved_by_name?: string | null;
  resolved_at?: string | null;
  resolution_note?: string | null;
}

/**
 * A clock-in or clock-out location, as reported by the employee's phone.
 *
 * Client-reported and unverifiable: the device says where it is and the server
 * cannot check. `accuracy_m` and `is_mocked` are part of the record for that
 * reason — a fix without its error radius reads as far more certain than it is,
 * and a mocked one should never be presented as genuine.
 *
 * `status` is what makes an absent fix readable:
 *   ok            coordinates were captured
 *   denied        the employee did not grant location permission
 *   unavailable   permission given, but no fix (indoors, services off)
 *   not_required  a work-from-home day, which is never geotagged
 */
export interface TimesheetGeo {
  status: 'ok' | 'denied' | 'unavailable' | 'not_required';
  lat: number | null;
  lng: number | null;
  accuracy_m: number | null;
  captured_at: string | null;
  is_mocked: boolean | null;
}

export interface Timesheet {
  timesheet_id: number | null;
  user_id?: number;
  user_name?: string | null;
  work_date: string;
  work_location?: 'office' | 'wfh' | 'client';
  /** Which client — only meaningful when work_location is 'client'. */
  client_organization_id?: number | null;
  client_organization_name?: string | null;
  in_time?: string | null;
  out_time?: string | null;
  /** Where the device was when the clock time was set. Captured by the mobile
   *  app only — a browser has no GPS — so these are null for a day recorded on
   *  the web, and for any day predating geotagging. Read-only here. */
  in_geo?: TimesheetGeo | null;
  out_geo?: TimesheetGeo | null;
  working_minutes: number;
  task_minutes: number;
  unallocated_minutes: number;
  total_units: number;
  working_label?: string;
  task_label?: string;
  unallocated_label?: string;
  avg_minutes_per_unit?: number | null;
  units_per_hour?: number | null;
  status: string;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  reviewed_by_name?: string | null;
  decision_note?: string | null;
  notes?: string | null;
  entry_count?: number;
  is_editable?: boolean;
  error_count?: number;
  warning_count?: number;
  is_weekend?: boolean;
  is_missing?: boolean;
  entries?: TimesheetEntryRow[];
  exceptions?: TimesheetException[];
  history?: { action: string; actor_name?: string | null; comments?: string | null; created_at: string }[];
}

export const timesheetService = {
  // exclude_drafts: a team view shows submitted work only. Without it the
  // server still hides OTHER people's drafts, which is the floor it enforces
  // for everyone; this drops the viewer's own too.
  list: (params?: { date_from?: string; date_to?: string; user_id?: number; status?: string;
                    organization_id?: number; only_exceptions?: boolean; exclude_drafts?: boolean;
                    limit?: number }) =>
    api.get<Timesheet[]>('/timesheets/', { params }),
  // Everyone whose timesheets you may read. Asked for directly rather than
  // inferred from a page of rows, which made the filter depend on the date
  // window, the row cap and the org filter all at once.
  employees: () => api.get<{ user_id: number; name: string }[]>('/timesheets/employees'),
  calendar: (date_from: string, date_to: string, user_id?: number) =>
    api.get<Timesheet[]>('/timesheets/calendar', { params: { date_from, date_to, user_id } }),
  get: (id: number) => api.get<Timesheet>(`/timesheets/${id}`),
  create: (data: { work_date: string; work_location?: string; in_time?: string; out_time?: string }) =>
    api.post<Timesheet>('/timesheets/', data),
  save: (id: number, data: { work_location?: string; client_organization_id?: number | null;
                             in_time?: string | null; out_time?: string | null;
                             notes?: string; entries?: TimesheetEntryRow[] }) =>
    api.put<Timesheet>(`/timesheets/${id}`, data),
  submit: (id: number) => api.post<Timesheet>(`/timesheets/${id}/submit`),
  claimReview: (id: number) => api.post<Timesheet>(`/timesheets/${id}/review`, {}),
  approve: (id: number, note?: string) => api.post<Timesheet>(`/timesheets/${id}/approve`, { note }),
  reject: (id: number, note: string) => api.post<Timesheet>(`/timesheets/${id}/reject`, { note }),
  reopen: (id: number, note: string) => api.post<Timesheet>(`/timesheets/${id}/reopen`, { note }),
  taskTypes: (includeInactive?: boolean) =>
    api.get<TimesheetTaskType[]>('/timesheets/task-types', { params: includeInactive ? { include_inactive: true } : {} }),
  // department_id is required: a task type belongs to exactly one department.
  createTaskType: (data: { name: string; uom: string; description?: string; department_id: number }) =>
    api.post<TimesheetTaskType>('/timesheets/task-types', data),
  // Omit department_id to leave it as it is; sending null is rejected, since a
  // type cannot be un-assigned once it belongs to a department.
  updateTaskType: (id: number, data: { name: string; uom: string; description?: string; is_active?: boolean; department_id?: number }) =>
    api.put<TimesheetTaskType>(`/timesheets/task-types/${id}`, data),
  deactivateTaskType: (id: number) => api.delete(`/timesheets/task-types/${id}`),
  exceptions: (params?: { date_from?: string; date_to?: string; user_id?: number; organization_id?: number;
                          code?: string; severity?: string; status?: string }) =>
    api.get<TimesheetException[]>('/timesheets/exceptions', { params }),
  resolveException: (id: number, status: string, note?: string) =>
    api.post<TimesheetException>(`/timesheets/exceptions/${id}/resolve`, { status, note }),
  summary: (params?: { date_from?: string; date_to?: string; user_id?: number }) =>
    api.get<any>('/timesheets/summary', { params }),
  // Report export — POSTs the on-screen rows and gets back an xlsx (the server
  // falls back to csv if openpyxl is missing), mirroring the tasks export.
  exportXlsx: (rows: any[], fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.post('/timesheets/export', rows, { params: { fmt }, responseType: 'blob', timeout: 60000 }),
};

// ─── Compliance ─────
export const complianceService = {
  getDeadlines: (params?: { category?: string; days_ahead?: number; include_overdue?: boolean; days_back?: number; organization_id?: number }) =>
    api.get('/compliance/deadlines', { params }),
  exportDeadlinesXlsx: (rows: any[], fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.post('/compliance/deadlines/export', rows, { params: { fmt }, responseType: 'blob', timeout: 60000 }),
  getLatestRun: () =>
    api.get('/compliance/latest-run'),
  getRuns: (params?: { skip?: number; limit?: number }) =>
    api.get('/compliance/runs', { params }),
  getRun: (runId: number) =>
    api.get(`/compliance/runs/${runId}`),
  triggerRun: () =>
    api.post('/compliance/runs/trigger'),
  getConfig: () =>
    api.get('/compliance/config'),
  updateConfig: (data: { is_enabled?: boolean; interval_hours?: number; sources?: Array<{ name: string; url: string; enabled: boolean }> }) =>
    api.put('/compliance/config', data),
  // Phase 7 item 16d — mark status per deadline
  updateDeadlineStatus: (deadlineId: number, user_status: string) =>
    api.put(`/compliance/deadlines/${deadlineId}/status`, { user_status }),
  // Manual create/edit/delete of compliance deadlines. Replaces the AI
  // auto-fetcher; super_admin or tenant_admin only.
  createDeadline: (data: {
    due_date: string;
    category: string;
    description: string;
    source_name?: string;
    source_url?: string;
  }) => api.post('/compliance/deadlines', data),
  updateDeadline: (deadlineId: number, data: {
    due_date?: string;
    category?: string;
    description?: string;
    source_name?: string;
    source_url?: string;
  }) => api.put(`/compliance/deadlines/${deadlineId}`, data),
  deleteDeadline: (deadlineId: number) =>
    api.delete(`/compliance/deadlines/${deadlineId}`),
  // Phase 7 item 16e — convert deadline to a task (priority + assignee added in Phase 15)
  convertDeadlineToTask: (deadlineId: number, options?: { priority?: string; assignee_user_id?: number }) =>
    api.post(`/compliance/deadlines/${deadlineId}/to-task`, options || {}),
  // Phase 7b — auditor-uploaded compliance activities
  listActivities: (include_inactive?: boolean) =>
    api.get('/compliance/activities', { params: { include_inactive } }),
  createActivity: (data: any) =>
    api.post('/compliance/activities', data),
  updateActivity: (id: number, data: any) =>
    api.put(`/compliance/activities/${id}`, data),
  deleteActivity: (id: number) =>
    api.delete(`/compliance/activities/${id}`),
};

export const masterDataService = {
  list: (params?: { organization_id?: number }) =>
    api.get('/master-data/', { params }),
  upload: (formData: FormData) =>
    api.post('/master-data/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (docId: number) =>
    `${API_HOST}/api/v1/master-data/${docId}/download`,
  // Phase 13 — fetch a direct URL (GCS signed or local download path) for reliable download
  getDownloadUrl: (docId: number) =>
    api.get(`/master-data/${docId}/download-url`),
  delete: (docId: number) =>
    api.delete(`/master-data/${docId}`),
  // Phase 8 item 18b — update Nature/Description
  update: (docId: number, description: string) => {
    const fd = new FormData();
    fd.append('description', description);
    return api.put(`/master-data/${docId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ===== Phase 17.1 — Insurance Management =====
export interface InsuranceCreatePayload {
  organization_id?: number;
  policy_number: string;
  policy_name: string;
  policy_type: string;
  insured_subject?: string;
  insured_subject_type?: string;
  insured_value?: number | string;
  currency?: string;
  division_or_project?: string;
  provider_name: string;
  provider_contact?: string;
  broker_name?: string;
  broker_contact?: string;
  policy_start_date: string;  // YYYY-MM-DD
  policy_end_date: string;
  premium_amount?: number | string;
  premium_frequency?: string;
  person_responsible_user_id?: number;
  escalation_user_id?: number;
  escalation_user_ids?: number[];
  is_personal?: boolean;
  document_location?: string;
  document_custodian_user_id?: number;
  property_id?: number;
  notes?: string;
}

export interface InsuranceUpdatePayload extends Partial<InsuranceCreatePayload> {
  status?: string;
}

export interface InsuranceRenewPayload {
  policy_start_date?: string;
  policy_end_date: string;
  insured_value?: number | string;
  premium_amount?: number | string;
  notes?: string;
  provider_name?: string;
  provider_contact?: string;
  broker_name?: string;
  broker_contact?: string;
}

/** Per-document people/custody fields shared by all record modules (Phase B). */
export interface RecordDocPeoplePayload {
  responsible_user_id?: number;
  escalation_user_id?: number;
  escalation_user_ids?: number[];
  custodian_user_id?: number;
  custodian_external_name?: string;
  custodian_external_phone?: string;
  location_id?: number;
  location_text?: string;
}

/** Append the per-document people/custody fields to a multipart FormData. */
export function appendDocPeople(fd: FormData, o?: RecordDocPeoplePayload) {
  if (!o) return;
  if (o.responsible_user_id != null) fd.append('responsible_user_id', String(o.responsible_user_id));
  if (o.escalation_user_id != null) fd.append('escalation_user_id', String(o.escalation_user_id));
  if (o.escalation_user_ids && o.escalation_user_ids.length) fd.append('escalation_user_ids', o.escalation_user_ids.join(','));
  if (o.custodian_user_id != null) fd.append('custodian_user_id', String(o.custodian_user_id));
  if (o.custodian_external_name) fd.append('custodian_external_name', o.custodian_external_name);
  if (o.custodian_external_phone) fd.append('custodian_external_phone', o.custodian_external_phone);
  if (o.location_id != null) fd.append('location_id', String(o.location_id));
  if (o.location_text) fd.append('location_text', o.location_text);
}

export const insuranceService = {
  list: (params?: {
    organization_id?: number;
    status?: string;
    policy_type?: string;
    property_id?: number;
    expiring_within_days?: number;
    overdue?: boolean;
    search?: string;
    skip?: number;
    limit?: number;
  }) => api.get('/insurances/', { params }),

  get: (id: number) => api.get(`/insurances/${id}`),
  audit: (id: number) => api.get(`/insurances/${id}/audit`),
  entities: () => api.get('/insurances/entities'),
  create: (data: InsuranceCreatePayload) => api.post('/insurances/', data),
  update: (id: number, data: InsuranceUpdatePayload) => api.put(`/insurances/${id}`, data),
  remove: (id: number) => api.delete(`/insurances/${id}`),

  renew: (id: number, data: InsuranceRenewPayload) =>
    api.post(`/insurances/${id}/renew`, data),
  chain: (id: number) => api.get(`/insurances/${id}/chain`),

  upcoming: (days: number = 30, organizationId?: number) => api.get('/insurances/upcoming', { params: { days, organization_id: organizationId } }),
  expired: (organizationId?: number) => api.get('/insurances/expired', { params: { organization_id: organizationId } }),
  pendingApproval: () => api.get('/insurances/pending-approval'),

  approve: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    api.post(`/insurances/${id}/approval`, { decision, reason }),

  // Lifecycle (item 16)
  close: (id: number) => api.post(`/insurances/${id}/close`),
  reassign: (data: { role: 'person_responsible' | 'escalation' | 'document_custodian'; to_user_id?: number | null; from_user_id?: number; insurance_ids?: number[] }) =>
    api.post('/insurances/reassign', data),
  reassignCandidates: (params: { role: string; from_user_id: number; status?: string }) =>
    api.get('/insurances/reassign/candidates', { params }),
  relocate: (data: { to_location_id?: number | null; from_location_id?: number; insurance_ids?: number[] }) =>
    api.post('/insurances/relocate', data),
  relocateCandidates: (params: { from_location_id: number; status?: string }) =>
    api.get('/insurances/relocate/candidates', { params }),
  documentsReport: (params?: { search?: string; due?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number }) =>
    api.get('/insurances/documents/report', { params }),
  documentsReportExport: (params?: { search?: string; due?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number }, fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.get('/insurances/documents/report/export', { params: { ...(params || {}), fmt }, responseType: 'blob' }),
  documentsReportPeople: (params?: { organization_id?: number }) =>
    api.get('/insurances/documents/report/people', { params }),
  documentRelocateCandidates: (params: { from_location_id: number; status?: string; organization_id?: number }) =>
    api.get('/insurances/documents/relocate/candidates', { params }),
  relocateDocuments: (data: { to_location_id: number; document_ids: number[] }) =>
    api.post('/insurances/documents/relocate', data),
  downloadDocumentsZipBlob: (documentIds: number[]) =>
    api.post('/insurances/documents/zip', { document_ids: documentIds }, { responseType: 'blob' }),
  pendingAcknowledgements: () => api.get('/insurances/pending-acknowledgements'),
  actOnAcknowledgement: (ackId: number, decision: 'approve' | 'reject', remarks?: string) =>
    api.post(`/insurances/acknowledgements/${ackId}`, { decision, remarks }),

  // Attachments
  listAttachments: (id: number, category?: string) =>
    api.get(`/insurances/${id}/attachments`, { params: category ? { category } : undefined }),

  uploadAttachment: (
    id: number,
    file: File,
    options?: {
      attachment_category?: string;
      year_from?: string;
      year_to?: string;
      notes?: string;
    } & RecordDocPeoplePayload,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    if (options?.attachment_category) fd.append('attachment_category', options.attachment_category);
    if (options?.year_from) fd.append('year_from', options.year_from);
    if (options?.year_to) fd.append('year_to', options.year_to);
    if (options?.notes) fd.append('notes', options.notes);
    appendDocPeople(fd, options);
    return api.post(`/insurances/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // large record uploads (up to 1 GB) — don't apply the 30s default
    });
  },

  updateAttachment: (attachmentId: number, data: any) =>
    api.put(`/insurances/attachments/${attachmentId}`, data),

  // ----- Claims -----------------------------------------------------------
  claimsOverview: (params?: { organization_id?: number; view?: string }) =>
    api.get('/insurances/claims/overview', { params }),
  claimsOverviewExport: (params?: { organization_id?: number; view?: string }) =>
    api.get('/insurances/claims/overview/export', { params, responseType: 'blob' }),
  listClaims: (insuranceId: number) => api.get(`/insurances/${insuranceId}/claims`),
  createClaim: (insuranceId: number, data: any) => api.post(`/insurances/${insuranceId}/claims`, data),
  getClaim: (claimId: number) => api.get(`/insurances/claims/${claimId}`),
  updateClaim: (claimId: number, data: any) => api.put(`/insurances/claims/${claimId}`, data),
  changeClaimStatus: (claimId: number, data: { status: string; approved_amount?: number; settled_date?: string; remarks?: string }) =>
    api.post(`/insurances/claims/${claimId}/status`, data),
  deleteClaim: (claimId: number) => api.delete(`/insurances/claims/${claimId}`),
  listClaimDocuments: (claimId: number) => api.get(`/insurances/claims/${claimId}/documents`),
  uploadClaimDocument: (claimId: number, file: File, options?: { document_type?: string; notes?: string }) => {
    const fd = new FormData();
    fd.append('file', file);
    if (options?.document_type) fd.append('document_type', options.document_type);
    if (options?.notes) fd.append('notes', options.notes);
    return api.post(`/insurances/claims/${claimId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
  },
  downloadClaimDocumentBlob: (documentId: number) =>
    api.get(`/insurances/claim-documents/${documentId}/download`, { responseType: 'blob' }),
  deleteClaimDocument: (documentId: number) => api.delete(`/insurances/claim-documents/${documentId}`),

  /** Legacy: returns the raw URL (works for GCS signed redirects, not for local). */
  downloadAttachment: (attachmentId: number) =>
    `${API_HOST}/api/v1/insurances/attachments/${attachmentId}/download`,

  /** Auth-aware: fetches as blob with JWT header, then triggers a browser download. */
  downloadAttachmentBlob: (attachmentId: number) =>
    api.get(`/insurances/attachments/${attachmentId}/download`, { responseType: 'blob' }),

  deleteAttachment: (attachmentId: number) =>
    api.delete(`/insurances/attachments/${attachmentId}`),

  // Org-level approval toggle
  getOrgSetting: (orgId: number) => api.get(`/insurances/settings/${orgId}`),
  updateOrgSetting: (orgId: number, requireInsuranceApproval: boolean) =>
    api.put(`/insurances/settings/${orgId}`, {
      require_insurance_approval: requireInsuranceApproval,
    }),

  // Excel export — uses axios so the JWT auth header is included; returns a Blob.
  // Caller is responsible for triggering the download (see helper below).
  exportXlsx: (params?: { organization_id?: number; status?: string; fmt?: string; columns?: string }) =>
    api.get('/insurances/export/xlsx', {
      params,
      responseType: 'blob',
    }),
  exportColumns: () => api.get('/insurances/export/columns'),
};

/** Trigger a browser download of an axios-fetched Blob.
 *  Used by the Excel export and any future binary-download endpoints. */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/** Send a test email to the current user to verify SMTP is configured. */
export const sendTestEmail = () => api.post('/notifications/test-email');

// ─── Per-tenant SMTP configuration (Settings → Configurations → Email) ──
export interface TenantEmailConfig {
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_user: string | null;
  smtp_from_email: string | null;
  smtp_from_name: string | null;
  use_tls: boolean | null;
  is_active: boolean;
  smtp_password_set: boolean;
  configured: boolean;
}
export interface TenantEmailConfigInput {
  smtp_host?: string | null;
  smtp_port?: number | null;
  smtp_user?: string | null;
  smtp_password?: string | null;   // blank = keep existing
  smtp_from_email?: string | null;
  smtp_from_name?: string | null;
  use_tls?: boolean | null;
  is_active: boolean;
}
export const tenantEmailConfigService = {
  get: () => api.get<TenantEmailConfig>('/tenant/email-config'),
  update: (data: TenantEmailConfigInput) => api.put<TenantEmailConfig>('/tenant/email-config', data),
  test: () => api.post('/tenant/email-config/test'),
};

/** Max upload size for all Records-module documents (Insurance, Real Estate,
 *  Licenses & Renewals, Agreements). Mirrors the backend cap. */
export const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024; // 1 GB

/** Returns an error message if the file is over the 1 GB limit, else null. */
export function checkUploadSize(file: File): string | null {
  if (file.size > MAX_UPLOAD_BYTES) {
    return `“${file.name}” is ${(file.size / 1024 / 1024).toFixed(1)} MB, which exceeds the 1 GB upload limit. Please choose a smaller file.`;
  }
  return null;
}

// ===== Phase 17.2 — Real Estate & Lease Documents =====
export interface PropertyCreatePayload {
  organization_id?: number;
  property_type: string;
  asset_class?: string;
  title: string;
  identification_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  description?: string;
  acquisition_date?: string;
  acquisition_value?: number | string;
  current_value?: number | string;
  currency?: string;
  lease_start_date?: string;
  lease_end_date?: string;
  rent_amount?: number | string;
  rent_frequency?: string;
  lessor_or_lessee_name?: string;
  lessor_or_lessee_contact?: string;
  person_responsible_user_id?: number;
  hard_copy_custodian_user_id?: number;
  hard_copy_custodian_external_name?: string;
  hard_copy_custodian_external_phone?: string;
  hard_copy_location?: string;
  hard_copy_location_id?: number;
  escalation_user_id?: number;
  escalation_user_ids?: number[];
  is_personal?: boolean;
  owner_user_text?: string;
  notes?: string;
}

export interface PropertyUpdatePayload extends Partial<PropertyCreatePayload> {
  status?: string;
}

export interface PropertyDocumentMetaPayload {
  document_category: string;
  document_name?: string;
  issued_date?: string;
  expiry_date?: string;
  issuing_authority?: string;
  reference_number?: string;
  copy_type?: string;
  description?: string;
  // Phase B — per-document people/custody
  responsible_user_id?: number;
  escalation_user_id?: number;
  escalation_user_ids?: number[];
  custodian_user_id?: number;
  custodian_external_name?: string;
  custodian_external_phone?: string;
  location_id?: number;
  location_text?: string;
}

export interface PropertyDocumentRenewPayload {
  issued_date: string;
  expiry_date?: string;
  reference_number?: string;
  copy_type?: string;
  description?: string;
}

export interface LicenseCreatePayload {
  organization_id?: number;
  license_number: string;
  title: string;
  license_type: string;
  division_or_project?: string;
  currency?: string;
  issuing_authority: string;
  issuing_authority_contact?: string;
  consultant_name?: string;
  consultant_contact?: string;
  issue_date: string;
  expiry_date: string;
  renewal_fee?: number | string;
  renewal_frequency?: string;
  action_mode?: string;
  renewal_url?: string;
  person_responsible_user_id?: number;
  escalation_user_id?: number;
  escalation_user_ids?: number[];
  is_personal?: boolean;
  owner_user_text?: string;
  document_location?: string;
  document_custodian_user_id?: number;
  notes?: string;
}
export interface LicenseUpdatePayload extends Partial<LicenseCreatePayload> { status?: string; }
export interface LicenseRenewPayload {
  issue_date?: string;
  expiry_date: string;
  renewal_fee?: number | string;
  notes?: string;
  issuing_authority?: string;
  issuing_authority_contact?: string;
  consultant_name?: string;
  consultant_contact?: string;
}

export const licenseService = {
  list: (params?: { organization_id?: number; status?: string; license_type?: string; expiring_within_days?: number; overdue?: boolean; search?: string; skip?: number; limit?: number }) =>
    api.get('/licenses/', { params }),
  get: (id: number) => api.get(`/licenses/${id}`),
  audit: (id: number) => api.get(`/licenses/${id}/audit`),
  entities: () => api.get('/licenses/entities'),
  create: (data: LicenseCreatePayload) => api.post('/licenses/', data),
  update: (id: number, data: LicenseUpdatePayload) => api.put(`/licenses/${id}`, data),
  remove: (id: number) => api.delete(`/licenses/${id}`),
  renew: (id: number, data: LicenseRenewPayload) => api.post(`/licenses/${id}/renew`, data),
  close: (id: number, data?: { notes?: string }) => api.post(`/licenses/${id}/close`, data || {}),
  chain: (id: number) => api.get(`/licenses/${id}/chain`),
  reassign: (data: { role: 'person_responsible' | 'escalation' | 'document_custodian'; to_user_id?: number | null; from_user_id?: number; license_ids?: number[] }) =>
    api.post('/licenses/reassign', data),
  reassignCandidates: (params: { role: string; from_user_id: number; status?: string }) =>
    api.get('/licenses/reassign/candidates', { params }),
  relocate: (data: { to_location_id?: number | null; from_location_id?: number; license_ids?: number[] }) =>
    api.post('/licenses/relocate', data),
  relocateCandidates: (params: { from_location_id: number; status?: string }) =>
    api.get('/licenses/relocate/candidates', { params }),
  documentsReport: (params?: { search?: string; due?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number }) =>
    api.get('/licenses/documents/report', { params }),
  documentsReportExport: (params?: { search?: string; due?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number }, fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.get('/licenses/documents/report/export', { params: { ...(params || {}), fmt }, responseType: 'blob' }),
  documentsReportPeople: (params?: { organization_id?: number }) =>
    api.get('/licenses/documents/report/people', { params }),
  documentRelocateCandidates: (params: { from_location_id: number; status?: string; organization_id?: number }) =>
    api.get('/licenses/documents/relocate/candidates', { params }),
  relocateDocuments: (data: { to_location_id: number; document_ids: number[] }) =>
    api.post('/licenses/documents/relocate', data),
  downloadDocumentsZipBlob: (documentIds: number[]) =>
    api.post('/licenses/documents/zip', { document_ids: documentIds }, { responseType: 'blob' }),
  pendingApproval: () => api.get('/licenses/pending-approval'),
  approve: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    api.post(`/licenses/${id}/approve`, { decision, reason }),
  getOrgSetting: (orgId: number) => api.get(`/licenses/settings/${orgId}`),
  updateOrgSetting: (orgId: number, val: boolean) =>
    api.put(`/licenses/settings/${orgId}`, { require_license_approval: val }),
  exportXlsx: (params?: { organization_id?: number; status?: string; fmt?: string; columns?: string }) =>
    api.get('/licenses/export/xlsx', { params, responseType: 'blob' }),
  exportColumns: () => api.get('/licenses/export/columns'),
  pendingAcknowledgements: () => api.get('/licenses/pending-acknowledgements'),
  actOnAcknowledgement: (ackId: number, decision: 'approve' | 'reject', remarks?: string) =>
    api.post(`/licenses/acknowledgements/${ackId}`, { decision, remarks }),
  listAttachments: (id: number) => api.get(`/licenses/${id}/attachments`),
  uploadAttachment: (id: number, file: File, options?: { attachment_category?: string; notes?: string; expiry_date?: string } & RecordDocPeoplePayload) => {
    const fd = new FormData();
    fd.append('file', file);
    if (options?.attachment_category) fd.append('attachment_category', options.attachment_category);
    if (options?.notes) fd.append('notes', options.notes);
    if (options?.expiry_date) fd.append('expiry_date', options.expiry_date);
    appendDocPeople(fd, options);
    return api.post(`/licenses/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
  },
  updateAttachment: (attachmentId: number, data: any) =>
    api.put(`/licenses/attachments/${attachmentId}`, data),
  downloadAttachmentBlob: (attachmentId: number) =>
    api.get(`/licenses/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attachmentId: number) => api.delete(`/licenses/attachments/${attachmentId}`),
};

export interface AgreementCreatePayload {
  organization_id?: number;
  agreement_type: string;
  title: string;
  reference_number?: string;
  copy_type?: string;
  description?: string;
  counterparty_name?: string;
  counterparty_contact?: string;
  effective_date?: string;
  expiry_date?: string;
  contract_value?: number | string;
  currency?: string;
  renewal_frequency?: string;
  auto_renewal?: boolean;
  renewal_notice_days?: number;
  person_responsible_user_id?: number;
  escalation_user_id?: number;
  escalation_user_ids?: number[];
  hard_copy_custodian_user_id?: number;
  hard_copy_custodian_external_name?: string;
  hard_copy_custodian_external_phone?: string;
  hard_copy_location_id?: number;
  is_personal?: boolean;
  owner_user_text?: string;
  notes?: string;
}
export interface AgreementUpdatePayload extends Partial<AgreementCreatePayload> { status?: string; }

export const agreementService = {
  list: (params?: { organization_id?: number; status?: string; agreement_type?: string; expiring_within_days?: number; overdue?: boolean; search?: string; skip?: number; limit?: number }) => api.get('/agreements/', { params }),
  get: (id: number) => api.get(`/agreements/${id}`),
  audit: (id: number) => api.get(`/agreements/${id}/audit`),
  entities: () => api.get('/agreements/entities'),
  create: (data: AgreementCreatePayload) => api.post('/agreements/', data),
  update: (id: number, data: AgreementUpdatePayload) => api.put(`/agreements/${id}`, data),
  remove: (id: number) => api.delete(`/agreements/${id}`),
  close: (id: number, data: { handover_date?: string; handed_over_to?: string; notes?: string }) => api.post(`/agreements/${id}/close`, data),
  renew: (id: number, data: any) => api.post(`/agreements/${id}/renew`, data),
  chain: (id: number) => api.get(`/agreements/${id}/chain`),
  reassign: (data: { role: 'person_responsible' | 'escalation' | 'hard_copy_custodian'; to_user_id?: number | null; from_user_id?: number; agreement_ids?: number[] }) => api.post('/agreements/reassign', data),
  reassignCandidates: (params: { role: string; from_user_id: number; status?: string }) =>
    api.get('/agreements/reassign/candidates', { params }),
  relocate: (data: { to_location_id?: number | null; from_location_id?: number; agreement_ids?: number[] }) =>
    api.post('/agreements/relocate', data),
  relocateCandidates: (params: { from_location_id: number; status?: string }) =>
    api.get('/agreements/relocate/candidates', { params }),
  pendingApproval: () => api.get('/agreements/pending-approval'),
  approve: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    api.post(`/agreements/${id}/approve`, { decision, reason }),
  getOrgSetting: (orgId: number) => api.get(`/agreements/settings/${orgId}`),
  updateOrgSetting: (orgId: number, val: boolean) =>
    api.put(`/agreements/settings/${orgId}`, { require_agreement_approval: val }),
  exportXlsx: (params?: { organization_id?: number; status?: string; fmt?: string; columns?: string }) =>
    api.get('/agreements/export/xlsx', { params, responseType: 'blob' }),
  exportColumns: () => api.get('/agreements/export/columns'),
  pendingAcknowledgements: () => api.get('/agreements/pending-acknowledgements'),
  actOnAcknowledgement: (ackId: number, decision: 'approve' | 'reject', remarks?: string) => api.post(`/agreements/acknowledgements/${ackId}`, { decision, remarks }),
  listDocuments: (id: number) => api.get(`/agreements/${id}/documents`),
  uploadDocument: (id: number, file: File, meta?: { document_category?: string; document_name?: string } & RecordDocPeoplePayload) => {
    const fd = new FormData(); fd.append('file', file);
    if (meta?.document_category) fd.append('document_category', meta.document_category);
    if (meta?.document_name) fd.append('document_name', meta.document_name);
    appendDocPeople(fd, meta);
    return api.post(`/agreements/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 });
  },
  updateDocument: (documentId: number, data: any) =>
    api.put(`/agreements/documents/${documentId}`, data),
  downloadDocumentBlob: (documentId: number) => api.get(`/agreements/documents/${documentId}/download`, { responseType: 'blob' }),
  deleteDocument: (documentId: number) => api.delete(`/agreements/documents/${documentId}`),
  documentsReport: (params?: { agreement_type?: string; search?: string; due?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number }) => api.get('/agreements/documents/report', { params }),
  documentsReportExport: (params?: { agreement_type?: string; search?: string; due?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number }, fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') => api.get('/agreements/documents/report/export', { params: { ...(params || {}), fmt }, responseType: 'blob' }),
  documentsReportPeople: (params?: { organization_id?: number }) => api.get('/agreements/documents/report/people', { params }),
  documentRelocateCandidates: (params: { from_location_id: number; status?: string; organization_id?: number }) => api.get('/agreements/documents/relocate/candidates', { params }),
  relocateDocuments: (data: { to_location_id: number; document_ids: number[] }) => api.post('/agreements/documents/relocate', data),
  downloadDocumentsZipBlob: (documentIds: number[]) => api.post('/agreements/documents/zip', { document_ids: documentIds }, { responseType: 'blob' }),
};

export const propertyService = {
  list: (params?: {
    organization_id?: number;
    status?: string;
    property_type?: string;
    expiring_within_days?: number;
    overdue?: boolean;
    search?: string;
    skip?: number;
    limit?: number;
  }) => api.get('/properties/', { params }),

  get: (id: number) => api.get(`/properties/${id}`),
  subProperties: (id: number) => api.get(`/properties/${id}/sub-properties`),
  audit: (id: number) => api.get(`/properties/${id}/audit`),
  downloadAuditNote: (id: number, auditId: number) =>
    api.get(`/properties/${id}/audit/${auditId}/note`, { responseType: 'blob' }),
  entities: () => api.get('/properties/entities'),
  locations: (includeInactive = false) =>
    api.get('/properties/locations', { params: includeInactive ? { include_inactive: true } : undefined }),
  createLocation: (name: string) => api.post('/properties/locations', { name }),
  updateLocation: (id: number, data: { name?: string; is_active?: boolean }) =>
    api.put(`/properties/locations/${id}`, data),
  create: (data: PropertyCreatePayload) => api.post('/properties/', data),
  update: (id: number, data: PropertyUpdatePayload) => api.put(`/properties/${id}`, data),
  remove: (id: number) => api.delete(`/properties/${id}`),

  // Lifecycle (Slice 2)
  close: (id: number, data: { handover_date?: string; handed_over_to?: string; notes?: string }) =>
    api.post(`/properties/${id}/close`, data),
  reassign: (data: { role: 'person_responsible' | 'escalation' | 'hard_copy_custodian'; to_user_id?: number | null; from_user_id?: number; property_ids?: number[] }) =>
    api.post('/properties/reassign', data),
  reassignCandidates: (params: { role: string; from_user_id: number; status?: string; organization_id?: number }) =>
    api.get('/properties/reassign/candidates', { params }),
  documentsReportPeople: (params?: { organization_id?: number }) =>
    api.get('/properties/documents/report/people', { params }),
  documentRelocateCandidates: (params: { from_location_id: number; status?: string; organization_id?: number }) =>
    api.get('/properties/documents/relocate/candidates', { params }),
  relocateDocuments: (data: { to_location_id: number; document_ids: number[] }) =>
    api.post('/properties/documents/relocate', data),
  relocate: (data: { to_location_id?: number | null; from_location_id?: number; property_ids?: number[] }) =>
    api.post('/properties/relocate', data),
  relocateCandidates: (params: { from_location_id: number; status?: string; organization_id?: number }) =>
    api.get('/properties/relocate/candidates', { params }),
  pendingApproval: () => api.get('/properties/pending-approval'),
  approve: (id: number, decision: 'approve' | 'reject', reason?: string) =>
    api.post(`/properties/${id}/approve`, { decision, reason }),
  getOrgSetting: (orgId: number) => api.get(`/properties/settings/${orgId}`),
  updateOrgSetting: (orgId: number, val: boolean) =>
    api.put(`/properties/settings/${orgId}`, { require_realestate_approval: val }),
  pendingAcknowledgements: () => api.get('/properties/pending-acknowledgements'),
  actOnAcknowledgement: (ackId: number, decision: 'approve' | 'reject', remarks?: string) =>
    api.post(`/properties/acknowledgements/${ackId}`, { decision, remarks }),

  // Document-level reassignment — pick which documents transfer, per role.
  documentReassignPreview: (params: { property_id: number; role: string; from_user_id?: number }) =>
    api.get('/properties/documents/reassign/preview', { params }),
  documentReassignCandidates: (params: { role: string; from_user_id: number; status?: string; organization_id?: number }) =>
    api.get('/properties/documents/reassign/candidates', { params }),
  reassignDocuments: (data: { role: 'person_responsible' | 'escalation' | 'hard_copy_custodian'; to_user_id?: number | null; document_ids: number[] }) =>
    api.post('/properties/documents/reassign', data),
  docPendingAcknowledgements: () => api.get('/properties/documents/pending-acknowledgements'),
  docAckAcceptAll: (data: { property_id: number; assignment_role: string; remarks?: string }) =>
    api.post('/properties/documents/acknowledgements/accept-all', data),
  docAckRejectAll: (data: { property_id: number; assignment_role: string; remarks?: string }) =>
    api.post('/properties/documents/acknowledgements/reject-all', data),

  upcomingRenewals: (days: number = 30, organizationId?: number) =>
    api.get('/properties/upcoming-renewals', { params: { days, organization_id: organizationId } }),

  search: (q: string) => api.get('/properties/search', { params: { q } }),

  // Documents
  listDocuments: (propertyId: number, category?: string) =>
    api.get(`/properties/${propertyId}/documents`, { params: category ? { category } : undefined }),

  uploadDocument: (
    propertyId: number,
    file: File,
    meta: PropertyDocumentMetaPayload,
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_category', meta.document_category);
    if (meta.document_name) fd.append('document_name', meta.document_name);
    if (meta.issued_date) fd.append('issued_date', meta.issued_date);
    if (meta.expiry_date) fd.append('expiry_date', meta.expiry_date);
    if (meta.issuing_authority) fd.append('issuing_authority', meta.issuing_authority);
    if (meta.reference_number) fd.append('reference_number', meta.reference_number);
    if (meta.copy_type) fd.append('copy_type', meta.copy_type);
    if (meta.description) fd.append('description', meta.description);
    if (meta.responsible_user_id != null) fd.append('responsible_user_id', String(meta.responsible_user_id));
    if (meta.escalation_user_id != null) fd.append('escalation_user_id', String(meta.escalation_user_id));
    if (meta.escalation_user_ids && meta.escalation_user_ids.length) fd.append('escalation_user_ids', meta.escalation_user_ids.join(','));
    if (meta.custodian_user_id != null) fd.append('custodian_user_id', String(meta.custodian_user_id));
    if (meta.custodian_external_name) fd.append('custodian_external_name', meta.custodian_external_name);
    if (meta.custodian_external_phone) fd.append('custodian_external_phone', meta.custodian_external_phone);
    if (meta.location_id != null) fd.append('location_id', String(meta.location_id));
    if (meta.location_text) fd.append('location_text', meta.location_text);
    return api.post(`/properties/${propertyId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // large record uploads (up to 1 GB) — don't apply the 30s default
    });
  },

  // Large-document (1GB) path: the bytes were streamed browser→bucket via
  // /uploads; this attaches the finished object using its upload_session_id.
  completeDocumentUpload: (
    propertyId: number,
    uploadSessionId: number,
    meta: PropertyDocumentMetaPayload,
  ) =>
    api.post(`/properties/${propertyId}/documents/complete-upload`, {
      upload_session_id: uploadSessionId,
      document_category: meta.document_category,
      document_name: meta.document_name,
      issued_date: meta.issued_date,
      expiry_date: meta.expiry_date,
      issuing_authority: meta.issuing_authority,
      reference_number: meta.reference_number,
      copy_type: meta.copy_type,
      description: meta.description,
      responsible_user_id: meta.responsible_user_id,
      escalation_user_id: meta.escalation_user_id,
      escalation_user_ids: meta.escalation_user_ids ? meta.escalation_user_ids.join(',') : undefined,
      custodian_user_id: meta.custodian_user_id,
      custodian_external_name: meta.custodian_external_name,
      custodian_external_phone: meta.custodian_external_phone,
      location_id: meta.location_id,
      location_text: meta.location_text,
    }),

  updateDocument: (documentId: number, data: any) =>
    api.put(`/properties/documents/${documentId}`, data),

  deleteDocument: (documentId: number) =>
    api.delete(`/properties/documents/${documentId}`),

  renewDocument: (documentId: number, payload: PropertyDocumentRenewPayload, file?: File) => {
    const fd = new FormData();
    fd.append('issued_date', payload.issued_date);
    if (payload.expiry_date) fd.append('expiry_date', payload.expiry_date);
    if (payload.reference_number) fd.append('reference_number', payload.reference_number);
    if (payload.description) fd.append('description', payload.description);
    if (file) fd.append('file', file);
    return api.post(`/properties/documents/${documentId}/renew`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Legacy: returns the raw URL (works for GCS signed redirects, not for local). */
  downloadDocument: (documentId: number) =>
    `${API_HOST}/api/v1/properties/documents/${documentId}/download`,

  /** Auth-aware: fetches as blob with JWT header, then triggers a browser download. */
  downloadDocumentBlob: (documentId: number) =>
    api.get(`/properties/documents/${documentId}/download`, { responseType: 'blob' }),

  // Report + ZIP (item 13)
  documentsReport: (params?: { search?: string; due?: string; property_id?: number; property_type?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number; skip?: number; limit?: number }) =>
    api.get('/properties/documents/report', { params }),
  documentsReportExport: (params?: { search?: string; due?: string; property_id?: number; property_type?: string; document_category?: string; status?: string; organization_id?: number; responsible_user_id?: number; escalation_user_id?: number; custodian_user_id?: number; location_id?: number; skip?: number; limit?: number }, fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.get('/properties/documents/report/export', { params: { ...(params || {}), fmt }, responseType: 'blob' }),
  downloadDocumentsZipBlob: (documentIds: number[]) =>
    api.post('/properties/documents/zip', { document_ids: documentIds }, { responseType: 'blob' }),

  exportXlsx: (params?: { organization_id?: number; status?: string; fmt?: string; columns?: string }) =>
    api.get('/properties/export/xlsx', { params, responseType: 'blob' }),
  exportColumns: () => api.get('/properties/export/columns'),
};

// ─── Document Search ───────────────────────────────────────────────────

export interface SearchParams {
  q?: string;
  category?: string;
  source_type?: string;  // "property" or "insurance"
  page?: number;
  size?: number;
}

export interface SearchResult {
  id: number;
  source_type: string;          // "property" or "insurance"
  record_id: number;            // property_doc.document_id or insurance_att.attachment_id
  parent_record_id: number | null;  // property_id or insurance_id
  category: string | null;      // "Real Estate" or "Insurance"
  title: string | null;
  parent_title: string | null;
  file_name: string | null;
  file_type: string | null;
  metadata: Record<string, any> | null;
  snippet: string | null;
  rank: number | null;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  page: number;
  size: number;
  query: string | null;
}

export const documentSearch = {
  search: (params: SearchParams) =>
    api.get<SearchResponse>('/search', { params }),
  exportResults: (params: { q?: string; category?: string; source_type?: string }, fmt: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
    api.get('/search/export', { params: { ...params, fmt }, responseType: 'blob' }),
};

// ─── Scheduled / emailed reports ──────────────────────────────────────
export interface ScheduledReport {
  id: number;
  name: string;
  report_type: string;                 // compliance | insurance | license | agreement | realestate
  organization_id: number | null;
  fmt: string;                         // xlsx | pdf | csv
  frequency: string;                   // weekly | monthly
  day_of_week: number | null;          // 0=Mon..6=Sun (weekly)
  day_of_month: number | null;         // 1..28 (monthly)
  recipients: string[];
  is_active: boolean;
  last_run_on: string | null;
}
export type ScheduledReportInput = Omit<ScheduledReport, 'id' | 'last_run_on'>;

export const scheduledReportsService = {
  list: () => api.get<ScheduledReport[]>('/scheduled-reports'),
  create: (data: ScheduledReportInput) => api.post<ScheduledReport>('/scheduled-reports', data),
  update: (id: number, data: ScheduledReportInput) => api.put<ScheduledReport>(`/scheduled-reports/${id}`, data),
  remove: (id: number) => api.delete(`/scheduled-reports/${id}`),
  runNow: (id: number) => api.post(`/scheduled-reports/${id}/run-now`),
};

// ─── Storage Configuration ────────────────────────────────────────────

export interface StorageConfig {
  id: number;
  organization_id: number;
  purpose: string;        // "working" | "archive"
  provider: string;       // "gcs" | "s3" | "minio" | "azure" | "smb"
  bucket: string;
  region: string | null;
  root_prefix: string | null;
  endpoint_url: string | null;
  status: string;         // "active" | "suspended"
  preview_mode: string;   // "proxy" | "redirect"
  last_health_check_at: string | null;
  validated_at: string | null;
  // Credential is write-only; API returns only whether one is set + its scheme.
  credential_set?: boolean;
  credential_scheme?: string | null;   // enc | env | static | vault | raw
}

export interface StorageConfigCreate {
  provider: string;
  bucket: string;
  region?: string | null;
  root_prefix?: string;
  endpoint_url?: string | null;
  credential_ref?: string | null;
  preview_mode?: string;
  // S3/MinIO keys — encrypted at rest server-side into an enc:// credential_ref.
  aws_access_key_id?: string | null;
  aws_secret_access_key?: string | null;
}

export const storageConfigService = {
  getConfigs: (orgId: number) =>
    api.get<StorageConfig[]>(`/admin/orgs/${orgId}/storage-config`),

  setConfig: (orgId: number, purpose: string, body: StorageConfigCreate) =>
    api.post<StorageConfig>(`/admin/orgs/${orgId}/storage-config`, body, {
      params: { purpose },
    }),

  testConfig: (orgId: number, purpose: string) =>
    api.post<{ status: string; message: string; size: number }>(
      `/admin/orgs/${orgId}/storage-config/test`,
      null,
      { params: { purpose } },
    ),
};


// ---------------------------------------------------------------------
// Zoho Books integration (Phase 1)
// ---------------------------------------------------------------------
//
// Backend is at /api/v1/zoho. See ZOHO_BOOKS_INTEGRATION_DESIGN.md
// for the OAuth flow + push pipeline overview. This service maps 1:1
// to the 8 endpoints there.
//
// The OAuth handshake works via popup: the page calls getConnectUrl()
// to fetch a signed authorize URL, then window.open()s it. Zoho
// redirects back to /oauth/callback, which returns an HTML page that
// posts a `zoho-oauth-result` message back to the opener. The
// Integrations page listens for that message and re-fetches status.
// ---------------------------------------------------------------------

export interface ZohoStatusResponse {
  connected: boolean;
  zoho_org_id?: string | null;
  zoho_region?: string | null;
  zoho_edition?: string | null;
  connected_at?: string | null;
  connected_by_username?: string | null;
  last_sync_at?: string | null;
  last_error?: string | null;
  is_active: boolean;
  scopes_granted?: string | null;
  // The org's default Zoho-invoice status used by the Review & Post
  // screen. 'sent' | 'draft'. Null when not connected (we never
  // render the toggle in that case so the cast is fine).
  default_post_mode?: 'sent' | 'draft' | null;
  // When true, approval auto-enqueues the Zoho push as before.
  // When false, the approver is routed to /zoho-review. Null when
  // not connected.
  auto_push_on_approval?: boolean | null;
}

export interface ZohoCategorySetting {
  category_id: number;
  category_name: string;
  push_enabled: boolean;
}

// ---- Zoho Review & Post responses --------------------------------
//
// Shape returned by GET /documents/{id}/zoho/preview. The payload
// itself is the full Zoho /invoices request body that will be sent
// on Post — we type it loosely (Record<string, unknown>) because the
// frontend never invents the field names, only displays + edits the
// ones it knows (invoice_number, date, gst_no, place_of_supply,
// customer_id, gst_treatment, line_items[], custom_fields[]).
export interface ZohoPreviewIntegration {
  zoho_org_id: string;
  zoho_org_state_code?: string | null;
  post_mode: 'sent' | 'draft';
}

export interface ZohoContactPreview {
  contact_id?: string | null;
  would_create: boolean;
  name_overridden: boolean;
  zoho_contact_name: string;
  rule: string;
}

export interface ZohoTaxResolutionLine {
  line: number;
  rate: string | null;
  tax_id?: string | null;
  skip: boolean;
  // Why the line was skipped, when skip=true. Null on non-skipped
  // rows. Same shape as ZohoRecomputeLine.skip_reason — surfaced
  // on the badge.
  skip_reason?: string | null;
}

export interface ZohoTaxResolution {
  intra_or_inter?: 'intra' | 'inter' | null;
  classification_source?: string;
  lines: ZohoTaxResolutionLine[];
}

export interface ZohoPreflightRow {
  level: 'error' | 'warn' | 'info';
  code: string;
  message: string;
  // Present on rows that point at a specific line item (e.g.
  // 'line_skipped' rows from the tax resolver). 1-based to match the
  // editor's row numbering.
  line_index?: number;
}

export interface ZohoRawLineItem {
  line_data: Record<string, unknown>;
  line_number: number;
}

export interface ZohoPreviewResponse {
  document_id: number;
  integration: ZohoPreviewIntegration;
  payload: Record<string, unknown> | null;
  // PaperBridge line items in source shape (Item Description / HSN /
  // Quantity / Taxable Amount / Total / ...) for the editor to round-
  // trip via /zoho/post. The built Zoho payload is one-way; the user
  // edits in source shape and the mapper re-derives.
  raw_line_items: ZohoRawLineItem[];
  // Doc's stored extracted_fields keyed by field_name. Baseline for
  // the editor's diff-on-post.
  extracted_fields: Record<string, string>;
  contact_preview: ZohoContactPreview | null;
  tax_resolution: ZohoTaxResolution | null;
  preflight: ZohoPreflightRow[];
  can_post: boolean;
  // Vocabulary for the tax-routing override controls on the Review
  // screen. state_codes = 2-letter Indian state/UT codes Zoho accepts
  // on the place_of_supply field; gst_treatments = Zoho's enum.
  // Both come from the backend so the lists stay in lockstep.
  state_codes: string[];
  gst_treatments: string[];
  // Full tax catalogue for the per-line tax_id picker (Commit C).
  // Includes both intra (tax_group, CGST+SGST) and inter (tax, IGST)
  // rows so the picker can offer a cross-type override.
  tax_catalogue: ZohoTaxCatalogueRow[];
}

// Shape returned by POST /documents/{id}/zoho/post.
export interface ZohoPostResponse {
  status: 'succeeded' | 'failed';
  push_job_id: number;
  zoho_transaction_id?: string | null;
  zoho_transaction_number?: string | null;
  last_error?: string | null;
  post_mode: 'sent' | 'draft';
  message: string;
}

export interface ZohoPostRequest {
  post_mode?: 'sent' | 'draft' | null;
  field_overrides?: Record<string, string | null> | null;
  line_item_overrides?: Array<{ line_data: Record<string, unknown> }> | null;
  // Tax-routing overrides — all independently optional. When omitted
  // the backend auto-derives from the receiver GSTIN (current
  // behaviour). User picks any combination on the Review screen.
  override_place_of_supply?: string | null;
  override_gst_treatment?: 'business_gst' | 'business_none' | 'consumer' | null;
  force_create_contact?: boolean;
  // Direct Zoho contact_id the user picked from the search dropdown.
  // Skips the resolver entirely. Wins over force_create_contact if
  // both arrive.
  override_contact_id?: string | null;
  // Per-line tax_id overrides keyed by 1-based line number. Wins
  // over the resolver's auto-pick on that line. Lines not in the
  // map fall back to the resolver.
  line_item_tax_overrides?: Record<string, string> | null;
}

// One entry in the org's cached tax catalogue. Powers the per-line
// tax_id picker dropdown.
export interface ZohoTaxCatalogueRow {
  tax_id: string;
  tax_name: string;
  tax_percentage: string;
  // 'tax' = single IGST (inter-state). 'tax_group' = CGST+SGST
  // combo (intra-state). The picker filters by the current
  // classification but lets the user pick cross-type if they
  // explicitly want.
  tax_type: 'tax' | 'tax_group';
}

// One row in the Customer picker's dropdown. Returned by the new
// /zoho/search-contacts proxy. billing_state + place_of_contact are
// the two disambiguation fields the user typically scans.
export interface ZohoContactSearchHit {
  contact_id: string;
  contact_name: string;
  gst_no: string;
  billing_state: string;
  place_of_contact: string;
  gst_treatment: string;
}

// Shape returned by POST /documents/{id}/zoho/recompute-taxes.
// Permissive: tax_id may be null on a line whose computed rate
// doesn't match any cached Zoho tax — the editor flags that inline.
export interface ZohoRecomputeLine {
  line: number;
  rate: string | null;
  tax_id: string | null;
  tax_name: string | null;
  skip: boolean;
  // One-line human reason the row was skipped (Total<Taxable, missing
  // Quantity, etc.). Null when the line isn't skipped. Surfaced
  // inline as a tooltip + hint on the badge so users know exactly
  // what to fix.
  skip_reason?: string | null;
}

export interface ZohoRecomputeResponse {
  intra_or_inter: 'intra' | 'inter' | null;
  classification_source: string | null;
  lines: ZohoRecomputeLine[];
  unresolved: Array<{ line: number; rate: string }>;
  error: string | null;
}

export const zohoService = {
  getStatus: () =>
    api.get<ZohoStatusResponse>('/zoho/status'),

  // Returns { authorize_url: string } — caller does window.open(url).
  // /connect (returning 307) exists too but isn't useful from axios.
  getConnectUrl: () =>
    api.get<{ authorize_url: string }>('/zoho/connect/url'),

  disconnect: () =>
    api.post<{ message: string }>('/zoho/disconnect'),

  getCategorySettings: () =>
    api.get<{ categories: ZohoCategorySetting[] }>('/zoho/settings/categories'),

  updateCategorySetting: (categoryId: number, pushEnabled: boolean) =>
    api.put<{
      category_id: number;
      category_name: string;
      push_enabled: boolean;
    }>('/zoho/settings/categories', {
      category_id: categoryId,
      push_enabled: pushEnabled,
    }),

  // Update the org's default Post mode for the Zoho Review & Post
  // screen. tenant_admin only. The Review screen still lets the user
  // override per-doc; this is just what the radio defaults to.
  updateDefaultPostMode: (mode: 'sent' | 'draft') =>
    api.put<{ default_post_mode: 'sent' | 'draft' }>(
      '/zoho/settings/post-mode',
      { default_post_mode: mode },
    ),

  // Toggle auto-push-on-approval. tenant_admin only.
  // true  = approval enqueues + fires Zoho push as a background task
  //         (Phase 1 behaviour; legacy customers keep this on).
  // false = approval is silent; UI routes the approver to /zoho-review
  //         for the side-by-side review + Post-to-Zoho click.
  updateAutoPushOnApproval: (enabled: boolean) =>
    api.put<{ auto_push_on_approval: boolean }>(
      '/zoho/settings/auto-push',
      { auto_push_on_approval: enabled },
    ),

  // ── Custom field mapping (follow-up to Phase 1) ─────────────────
  //
  // Three flows on the Integrations page:
  //   1. Page load: getCategoryMappings(catId) for each opt-in
  //      category. Returns the PaperBridge fields + their current
  //      Zoho mapping (if any), plus the Zoho entity to filter the
  //      dropdown by.
  //   2. Dropdown populates from getZohoCustomFields(entity). Hits
  //      backend cache (1h TTL); pass refresh=true to force-pull
  //      from Zoho when the admin clicks "Refresh".
  //   3. Picking a value calls upsertMapping with the Zoho
  //      customfield_id. Picking "none" (null) deletes the row.

  getZohoCustomFields: (entity: string, refresh = false) =>
    api.get<ZohoCustomFieldsListResponse>(
      `/zoho/mappings/zoho-fields/${encodeURIComponent(entity)}`,
      { params: refresh ? { refresh: 'true' } : undefined },
    ),

  // Returns the hard-coded standard mappings for the entity
  // (PaperBridge field → Zoho field). Read-only product information,
  // not customer-configurable — UI renders as a static table above
  // the editable custom-field dropdowns. Cached forever in the
  // browser session because it only changes on a backend deploy.
  getStandardMappings: (entity: string) =>
    api.get<StandardMappingsResponse>(
      `/zoho/mappings/standard/${encodeURIComponent(entity)}`,
    ),

  getCategoryMappings: (categoryId: number) =>
    api.get<CategoryMappingsResponse>(`/zoho/mappings/list/${categoryId}`),

  upsertMapping: (
    categoryId: number,
    paperbridgeFieldId: number,
    zohoCustomfieldId: string | null,
  ) =>
    api.put<{
      mapping_id?: number;
      message?: string;
      paperbridge_field_id?: number;
      zoho_customfield_id?: string;
      zoho_customfield_label?: string;
      is_active?: boolean;
    }>('/zoho/mappings', {
      category_id: categoryId,
      paperbridge_field_id: paperbridgeFieldId,
      zoho_customfield_id: zohoCustomfieldId,
    }),

  deleteMapping: (mappingId: number) =>
    api.delete<{ message: string }>(`/zoho/mappings/${mappingId}`),

  // ── Zoho Review & Post (the side-by-side review screen) ──────────
  //
  // getDocumentZohoPreview: read-only build of the prospective Zoho
  // payload + preflight rows for the doc. Used to render the right
  // pane of the review screen on mount. Safe to call repeatedly —
  // backend has no /contacts POST or /invoices POST on this path.
  //
  // postDocumentToZoho: applies the user's edits to document_fields /
  // document_line_items and runs the push synchronously. Returns the
  // job's final state (succeeded / failed) once Zoho's HTTP round-
  // trip completes. The user clicked Post and is on the screen
  // waiting; do NOT wrap in a polling loop, the backend already does
  // the synchronous version.
  getDocumentZohoPreview: (documentId: number) =>
    api.get<ZohoPreviewResponse>(`/documents/${documentId}/zoho/preview`),

  postDocumentToZoho: (documentId: number, body: ZohoPostRequest) =>
    api.post<ZohoPostResponse>(`/documents/${documentId}/zoho/post`, body),

  // Live tax recompute — called from the line item editor as the user
  // edits rate / taxable / total. Read-only on the backend (no Zoho
  // POST). Permissive — returns tax_id=null on unresolved lines so
  // the UI can flag them inline rather than blocking.
  recomputeZohoTaxes: (
    documentId: number,
    body: {
      line_items: Array<{ line_data: Record<string, unknown> }>;
      field_overrides?: Record<string, string | null> | null;
      // Lets the live tax badges respond when the user picks a
      // place_of_supply on the Review screen without waiting for
      // Post. Same vocabulary as ZohoPostRequest.override_place_of_supply.
      override_place_of_supply?: string | null;
    },
  ) =>
    api.post<ZohoRecomputeResponse>(
      `/documents/${documentId}/zoho/recompute-taxes`,
      body,
    ),

  // Customer picker — type-ahead search proxying into Zoho's
  // /contacts?contact_name_contains. Read-only. The frontend
  // debounces typing and gates on length>=2 before calling.
  searchZohoContacts: (documentId: number, q: string) =>
    api.post<{
      contacts: ZohoContactSearchHit[];
      error: string | null;
    }>(`/documents/${documentId}/zoho/search-contacts`, { q }),
};

// ─── Field mapping types ──────────────────────────────────────────
export interface ZohoCustomFieldOut {
  customfield_id: string;
  label: string;
  data_type: string;     // 'string' | 'number' | 'date' | 'dropdown' | ...
  is_active: boolean;
  is_mandatory: boolean;
  placeholder?: string | null;
}

export interface ZohoCustomFieldsListResponse {
  entity: string;
  fields: ZohoCustomFieldOut[];
  fetched_at?: string | null;
}

export interface MappingCurrentZohoField {
  mapping_id: number;
  customfield_id: string;
  label: string;
  data_type: string;
  is_active: boolean;
}

export interface PaperBridgeFieldWithMapping {
  paperbridge_field_id: number;
  field_name: string;
  display_label?: string | null;
  field_type: string;
  is_required: boolean;
  mapped_to: MappingCurrentZohoField | null;
}

export interface CategoryMappingsResponse {
  category_id: number;
  category_name: string;
  zoho_entity: string | null;   // null = not supported in Phase 1
  fields: PaperBridgeFieldWithMapping[];
}

export interface StandardMappingOut {
  paperbridge_field: string;
  zoho_field: string;
  scope: 'header' | 'line_item';
  required: boolean;
  note?: string | null;
}

export interface StandardMappingsResponse {
  entity: string;
  mappings: StandardMappingOut[];
}

// ---------------------------------------------------------------------------
// Forensic Detection Layer — Phase 1
// See FORENSIC_DETECTION_DESIGN.md §8 for the API contract and §9 for how
// these fields drive the UI (shield badge + score gauge + findings list).
// ---------------------------------------------------------------------------

export type ForensicSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';
export type ForensicVerdict =
  | 'authentic'
  | 'inconclusive'
  | 'suspicious'
  | 'likely_forged';
export type ForensicJobStatus =
  | 'queued'
  | 'running'
  | 'done'
  | 'failed'
  | 'skipped';

export interface ForensicFinding {
  id: number;
  detector: string;
  severity: ForensicSeverity;
  raw_score: number | null;
  evidence: Record<string, unknown>;
  bbox: { x: number; y: number; w: number; h: number } | null;
  page_number: number | null;
  explanation: string;
  evidence_image_url: string | null;
  created_at: string;
}

export interface ForensicJob {
  id: number;
  document_id: number;
  tenant_id: number | null;
  status: ForensicJobStatus;
  triggered_by: string;
  detector_versions: Record<string, string>;
  final_score: number | null;
  verdict: ForensicVerdict | null;
  reasoning: string | null;
  cost_cents: number | null;
  error_message: string | null;
  queued_at: string;
  started_at: string | null;
  completed_at: string | null;
  findings: ForensicFinding[];
}

export interface ForensicRunRequest {
  only_detectors?: string[];
  triggered_by?: string;
}

export interface TenantForensicConfig {
  tenant_id: number;
  enabled: boolean;
  default_mode: 'off' | 'flag-only' | 'always-show';
  enabled_detectors: Record<string, boolean>;
  threshold_inconclusive: number;
  threshold_suspicious: number;
  threshold_likely_forged: number;
  auto_run_categories: string[];
  tier3_enabled: boolean;
  reasoning_provider: string | null;
  updated_by_user_id: number | null;
  updated_at: string;
}

// PATCH-style — every field optional, only send what you want to change.
export interface TenantForensicConfigUpdate {
  enabled?: boolean;
  default_mode?: 'off' | 'flag-only' | 'always-show';
  enabled_detectors?: Record<string, boolean>;
  threshold_inconclusive?: number;
  threshold_suspicious?: number;
  threshold_likely_forged?: number;
  auto_run_categories?: string[];
  tier3_enabled?: boolean;
  reasoning_provider?: 'anthropic' | 'openai' | 'gemini' | null;
}

export const forensicService = {
  /**
   * Trigger a forensic job. Idempotent — a queued/running job for the
   * same document is returned as-is. Runs the detectors asynchronously
   * server-side; the returned job will typically be status='queued' or
   * 'running'. Poll `getResult` to watch it complete.
   */
  run: (documentId: number, body: ForensicRunRequest = {}) =>
    api.post<ForensicJob>(`/documents/${documentId}/forensic/run`, body),

  /**
   * Latest forensic job + findings for a document. Returns 404 if the
   * document has never had a forensic job created.
   */
  getResult: (documentId: number) =>
    api.get<ForensicJob>(`/documents/${documentId}/forensic`),

  /** Super Admin — read a tenant's forensic config (returns defaults for a
   * new tenant that hasn't been configured yet, no need for POST-then-PUT). */
  getTenantConfig: (tenantId: number) =>
    api.get<TenantForensicConfig>(`/admin/tenants/${tenantId}/forensic/config`),

  /** Super Admin — patch a tenant's forensic config. Any field left off the
   * body is untouched. Creates the row if it doesn't yet exist. */
  updateTenantConfig: (tenantId: number, updates: TenantForensicConfigUpdate) =>
    api.put<TenantForensicConfig>(`/admin/tenants/${tenantId}/forensic/config`, updates),
};

// ─────────────────────────── Automation framework ──────────────────────────
// P1: read-only validation findings per document. Advisory — nothing here
// changes the document's workflow state. Dark until AUTOMATION_ENABLED, so most
// documents return an empty list and the panel renders nothing.

export type FindingSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
export type FindingStatus =
  | 'OPEN' | 'RESOLVING' | 'RESOLVED' | 'WAIVED' | 'BLOCKED' | 'REOPENED';

export interface AutomationFinding {
  id: number;
  document_id: number;
  type: string;
  category: string;
  severity: FindingSeverity;
  status: FindingStatus;
  validator_key: string;
  actual: any;
  expected: any;
  variance: any;
  variance_percent: number | null;
  message: string | null;
  evidence: Record<string, any>;
  created_at: string | null;
  updated_at: string | null;
}

export interface DocumentFindingsResponse {
  document_id: number;
  findings: AutomationFinding[];
}

export interface UploadPrecheckDoc {
  document_id: number;
  tenant_seq: number | null;
  file_name: string;
  submitted_at: string | null;
}

export interface UploadPrecheckResult {
  duplicate: (UploadPrecheckDoc & { message: string }) | null;
  same_name: UploadPrecheckDoc[];
}

export interface RecycleBinItem {
  document_id: number;
  tenant_seq: number | null;
  file_name: string;
  submitted_at: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  deleted_reason: string | null;
  days_left: number | null;
}

export interface RecycleBinResponse {
  retention_days: number;
  can_purge: boolean;
  documents: RecycleBinItem[];
  total?: number;            // everything in the bin, not just this page
}

export const findingsService = {
  /** List automation findings for a document (tenant-scoped, read-only). */
  listForDocument: (documentId: number, statusFilter?: FindingStatus) =>
    api.get<DocumentFindingsResponse>(`/documents/${documentId}/findings`, {
      params: statusFilter ? { status_filter: statusFilter } : {},
    }),
};

// ─────────────────────────── Rule Studio (automation P3) ────────────────────
// JSON-DSL policy rules: author (guided builder / DSL), publish, test & trace.
// Engine stays dark until AUTOMATION_ENABLED — authoring is safe meanwhile.

export type RuleStatus = 'DRAFT' | 'TESTED' | 'APPROVED' | 'PUBLISHED' | 'RETIRED';

export interface RuleClause {
  field?: string;
  operator?: string;
  value?: any;
  value_from?: string;
  all?: RuleClause[];
  any?: RuleClause[];
  not?: RuleClause;
}
export interface RuleCondition {
  all?: RuleClause[];
  any?: RuleClause[];
  not?: RuleClause;
}
export interface RuleAction {
  type: string;
  target?: string;
  value?: any;
  [k: string]: any;
}
export interface PolicyRule {
  id: number;
  tenant_id: number | null;
  policy_pack_id: number | null;
  name: string;
  description: string | null;
  rule_type: string;
  priority: number;
  specificity: number;
  scope: Record<string, any>;
  condition: RuleCondition;
  actions: RuleAction[];
  status: RuleStatus;
  version: number;
  created_at: string | null;
  updated_at: string | null;
}
export interface RuleTestResult {
  rules: Array<{
    rule_id: number;
    name: string;
    matched: boolean;
    matched_finding: string | null;
    trace: Array<{ field: string; operator: string; expected: any; actual: any; result: boolean }>;
  }>;
  decision: null | {
    outcome: string;
    rationale: string;
    actions: RuleAction[];
    target: string | null;
    requires_human: boolean;
    matched_rule_ids: number[];
  };
  findings: Array<{ type: string; severity: string; message: string | null }>;
}

export interface AutomationActivity {
  document_id: number;
  file_name: string | null;
  status: string | null;
  automation_hold: boolean;
  findings: Array<{ type: string; severity: string; status: string; message: string | null }>;
  decision: null | { outcome: string; rationale: string | null; requires_human: boolean };
  actions: Array<{ action_type: string; status: string; message: string | null }>;
}
export interface RuleConflict {
  kind: string;
  severity: 'high' | 'warning' | 'info';
  rules: Array<{ id: number; name: string }>;
  detail: string;
}
export interface RuleHealth {
  id: number;
  name: string;
  status: string;
  version: number;
  priority: number;
  matched_count: number;
  last_matched: string | null;
  never_matched: boolean;
}

export interface FindingTypeInfo {
  key: string;
  label: string;
  description?: string;
  active?: boolean;
  origin?: 'builtin' | 'custom';
  severity?: string;
  category?: string;
  muted?: boolean;        // built-in only: suppressed for this tenant
  overridden?: boolean;   // built-in only: has a tenant override
  editable?: boolean;     // built-in types can now be overridden from the UI
}
export interface FindingTypeOverrideBody {
  label?: string | null;
  description?: string | null;
  severity?: string | null;
  muted?: boolean;
}
export interface ValidatorInput {
  key: string;            // logical input key, e.g. "invoice.grand_total"
  label: string;
  group: string;
  scope: 'fact' | 'line';
  fallback: string;       // human hint of the built-in synonyms
  finding_types: string[];
  field_names: string[];  // this tenant's bound field names ([] = use heuristic)
}
export interface CustomCheck {
  id: number;
  finding_key: string;
  label: string;
  category: string;
  severity: string;
  description: string | null;
  condition: RuleCondition;
  enabled: boolean;
  updated_at: string | null;
}
export interface FieldPathOption {
  path: string; label: string; type?: string; description?: string; origin?: 'builtin' | 'custom';
}
export interface FactFields {
  facts: FieldPathOption[];
  expected: FieldPathOption[];
  external: FieldPathOption[];
  document: FieldPathOption[];
}

export interface TaxConfig {
  gst_slabs: number[];
  tds_sections: Record<string, { rate: number; threshold: number }>;
  tolerances: { gst_pct: number; tds_pct: number; department_auto_confidence: number };
}
export const taxConfigService = {
  get: () => api.get<{ config: TaxConfig; defaults: TaxConfig }>('/tax-config'),
  update: (body: Partial<TaxConfig>) =>
    api.put<{ config: TaxConfig; defaults: TaxConfig }>('/tax-config', body),
};

export interface ExternalConnector {
  id: number;
  name: string;
  kind: string;
  capabilities: string[];
  config: Record<string, any>;
  secret?: string | null;          // write-only; encrypted at rest, never returned
  organization_id: number | null;
  enabled: boolean;
  updated_at: string | null;
}
// ── Activations (unified per-org binding for workflows + rule-packs) ──
export const activationService = {
  list: () => api.get<{ activations: any[] }>('/activations'),
  options: () => api.get<{ workflows: any[]; rule_packs: any[]; rules: any[]; organizations: { id: number; name: string }[] }>('/activations/options'),
  create: (body: { kind: string; definition_id: number; organization_ids: number[]; trigger: any }) =>
    api.post<{ created: number[] }>('/activations', body),
  toggle: (id: number, enabled: boolean) => api.post(`/activations/${id}/toggle`, { enabled }),
  remove: (id: number) => api.delete(`/activations/${id}`),
};

// ── Document intelligence / insights ──────────────────────────────────
export const insightsService = {
  financial: (p: { from_date?: string; to_date?: string; organization_id?: number; approved_only?: boolean } = {}) =>
    api.get<any>('/insights/financial', { params: p }),
  risk: (p: { organization_id?: number } = {}) => api.get<any>('/insights/risk', { params: p }),
  categoryDirections: () => api.get<{ categories: any[] }>('/insights/category-directions'),
  setDirection: (categoryId: number, direction: string | null) =>
    api.patch(`/insights/category-directions/${categoryId}`, { direction }),
  seedDirections: () => api.post('/insights/financials/seed-directions'),
  backfill: () => api.post('/insights/financials/backfill'),
};

// ── Runs console + lineage (Phase 3-5) ────────────────────────────────
export interface DocumentPayment {
  reference: string;
  status: string;                      // pending | paid | failed | cancelled
  amount: number | null;
  currency: string;
  provider: string;
  direction: string;                   // collect (AR, customer pays) | pay (AP)
  payment_link: string | null;
  document_id: number | null;
  paid_at: string | null;
  /** How it was paid, recorded at confirmation. */
  settlement?: { utr?: string; paid_on?: string; mode?: string | null } | null;
  // Decided by the server, never re-derived here: the four-eyes rule on a
  // payment is the kind of thing that drifts the moment two places own it.
  can_confirm?: boolean;
  cannot_confirm_reason?: string | null;
}

export const paymentService = {
  forDocument: (documentId: number) =>
    api.get<{ payment: DocumentPayment | null }>(`/payments/document/${documentId}`),
  /** Manual confirmation: how the money arrived is required (UTR + date). */
  markPaid: (reference: string, body: { utr: string; paid_on?: string; mode?: string }) =>
    api.post<DocumentPayment>(`/payments/${reference}/mark-paid`, body),
};

export interface RunsFacets {
  statuses: { value: string; count: number }[];
  definitions: { id: number; name: string; count: number }[];
  organizations: { id: number; name: string; count: number }[];
  kinds: { value: string; count: number }[];
  total: number; truncated: boolean;
}

export const runsService = {
  list: (p: { kind?: string; organization_id?: number; status?: string; document_id?: number; definition_id?: number; limit?: number } = {}) =>
    api.get<{ runs: any[] }>('/runs', { params: p }),
  facets: () => api.get<RunsFacets>('/runs/facets'),
  workflow: (id: number) => api.get<any>(`/runs/workflow/${id}`),
  rule: (id: number) => api.get<any>(`/runs/rule/${id}`),
  document: (id: number) => api.get<any>(`/runs/document/${id}`),
};

// ── Workflow engine (P0–P4) ───────────────────────────────────────────
export interface WfConfigField {
  key: string; label: string; type: string;
  options?: string[]; placeholder?: string; help?: string;
}
export interface WfNodeType {
  type: string; group: string; label: string; backed_by: string | null;
  exec: string; side_effect: string; wired: boolean; action_type?: string;
  config_schema?: WfConfigField[];
}
export interface WfNode { key: string; type: string; name?: string; config?: any; assignment_rule?: any; }
export interface WfEdge { from: string; to: string; action?: string | null; condition?: any; order_index?: number; }
export const workflowEngineService = {
  nodeCatalog: () => api.get<{ groups: string[]; nodes: WfNodeType[]; count: number }>('/workflows/node-catalog'),
  status: () => api.get<{ enabled: boolean }>('/workflows/status'),
  listDefinitions: () => api.get<{ definitions: any[] }>('/workflows/definitions'),
  getDefinition: (id: number) => api.get<any>(`/workflows/definitions/${id}`),
  fieldPaths: () => api.get<{ paths: string[] }>('/workflows/field-paths'),
  pickers: () => api.get<{ departments: { id: number; name: string }[]; roles: string[]; users: { id: number; name: string; email: string; role: string }[]; permissions: string[] }>('/workflows/pickers'),
  createDefinition: (body: { key: string; name: string; entity_type?: string; scope?: any; inputs?: string[]; outputs?: string[]; nodes: WfNode[]; edges: WfEdge[] }) =>
    api.post<any>('/workflows/definitions', body),
  publish: (id: number) => api.post(`/workflows/definitions/${id}/publish`),
  deletePreview: (id: number) =>
    api.get<{ name: string; versions: number; live_runs: number; embedded_in: string[] }>(
      `/workflows/definitions/${id}/delete-preview`),
  /** Deletes the flow (every version): switched off, runs in progress cancelled. */
  deleteDefinition: (id: number) =>
    api.delete<{ deleted: number[]; cancelled_runs: number }>(
      `/workflows/definitions/${id}`, { params: { confirm: 'delete' } }),
  validate: (id: number) => api.get<{ errors: string[]; warnings: string[]; ok: boolean }>(`/workflows/definitions/${id}/validate`),
  simulate: (id: number, sample?: any) => api.post<{ version: number; steps: { node: string; type: string; label: string; note: string }[]; reached_terminal: boolean }>(`/workflows/definitions/${id}/simulate`, { sample: sample || null }),
  run: (id: number, document_id?: number) => api.post<any>(`/workflows/definitions/${id}/run`, { document_id }),
  listInstances: (params?: { definition_id?: number; document_id?: number; status_filter?: string }) =>
    api.get<{ instances: any[] }>('/workflows/instances', { params }),
  getInstance: (id: number) => api.get<any>(`/workflows/instances/${id}`),
  actTask: (taskId: number, action: string, payload: any = {}) =>
    api.post<any>(`/workflows/tasks/${taskId}/act`, { action, payload }),
  myTasks: () => api.get<{ tasks: { task_id: number; node_key: string; workflow: string; definition_id: number; instance_id: number; document_id: number | null; organization_id: number | null; organization_name: string | null; trigger_source: string | null; assignee_role: string | null; actions: string[]; created_at: string | null }[] }>('/workflows/my-tasks'),
  actMyTask: (taskId: number, action: string, payload: any = {}) =>
    api.post<{ instance_id: number; status: string }>(`/workflows/my-tasks/${taskId}/act`, { action, payload }),
  signal: (instId: number, event: string, data: any = {}) =>
    api.post<any>(`/workflows/instances/${instId}/signal`, { event, data }),
  listTriggers: (defId: number) => api.get<{ triggers: any[] }>(`/workflows/definitions/${defId}/triggers`),
  createTrigger: (defId: number, body: { kind?: string; event?: string; config?: any; enabled?: boolean }) =>
    api.post(`/workflows/definitions/${defId}/triggers`, body),
  deleteTrigger: (id: number) => api.delete(`/workflows/triggers/${id}`),
};

export const connectorsService = {
  list: () => api.get<{ connectors: ExternalConnector[]; kinds: string[] }>('/connectors'),
  create: (body: Partial<ExternalConnector>) => api.post<ExternalConnector>('/connectors', body),
  update: (id: number, body: Partial<ExternalConnector>) => api.put<ExternalConnector>(`/connectors/${id}`, body),
  remove: (id: number) => api.delete(`/connectors/${id}`),
  testPo: (id: number, po_number: string, organization_id?: number) =>
    api.post<{ found: boolean; po: any }>(`/connectors/${id}/test-po`, { po_number, organization_id }),
};


export interface ExternalParty {
  id: number;
  organization_id: number;
  party_type: 'vendor' | 'customer' | 'employee';
  name: string | null;
  phone: string | null;
  /** Contact address for notices and payment links (not an identity). */
  email?: string | null;
  gstin: string | null;
  pan: string | null;
  bank_account: string | null;
  bank_name?: string | null;
  bank_ifsc?: string | null;
  /** savings | current | cash_credit | overdraft | nre | nro */
  bank_account_type?: string | null;
  ledger_name: string | null;
  department_id: number | null;
  department_name?: string | null;
  assignee_user_id: number | null;
  status: string;
  source: string;
  is_mapped: boolean;
  /** Whether this party can sign in and file their own documents. The password
   *  is never carried here — issuing one returns it once and nothing can read
   *  it back afterwards. */
  portal_email?: string | null;
  portal_status?: string | null;
  portal_last_login_at?: string | null;
}

export interface ExternalMasterList {
  organization_id: number;
  parties: ExternalParty[];
  unmapped_count: number;
  party_types: string[];
  /** `initiators`: who documents from a party in this department are sent to.
   *  Empty means the department cannot be chosen until someone gets the role. */
  departments: { id: number; name: string; initiators?: string[] }[];
  account_types?: string[];
  /** May this user reveal a full PAN / account number (GET /{id}/sensitive)? */
  can_view_sensitive?: boolean;
  people: { user_id: number; name: string; department_id: number | null }[];
}

// External Master — vendors, customers and employees, and the department their
// documents go to. One row is both identity and destination.
export const externalMasterService = {
  list: (organizationId: number) =>
    api.get<ExternalMasterList>('/external-master', { params: { organization_id: organizationId } }),
  create: (body: Partial<ExternalParty> & { organization_id: number }) =>
    api.post<ExternalParty>('/external-master', body),
  update: (id: number, body: Partial<ExternalParty> & { organization_id: number }) =>
    api.put<ExternalParty>(`/external-master/${id}`, body),
  remove: (id: number) => api.delete(`/external-master/${id}`),
  /** Full PAN and account number; permission-gated and audited. */
  reveal: (id: number) =>
    api.get<{ id: number; pan: string | null; bank_account: string | null }>(`/external-master/${id}/sensitive`),
  importCsv: (organizationId: number, csvText: string) =>
    api.post<{ imported: number; skipped: number }>(
      '/external-master/import', { organization_id: organizationId, csv: csvText }),
  syncZoho: () =>
    api.post<{ imported: number; skipped: number; errors: string[] }>('/external-master/sync-zoho'),
  syncTally: () =>
    api.post<{ imported: number; skipped: number }>('/external-master/sync-tally'),
  requestTallyRefresh: () =>
    api.post<{ requested: number; agents_online: number; note: string }>(
      '/external-master/request-tally-refresh'),

  // Portal access. `issuePortalAccess` returns the temporary password ONCE —
  // it is stored hashed, so calling it again replaces the credential rather
  // than retrieving it, and every open session for that party is signed out.
  issuePortalAccess: (partyId: number, email: string) =>
    api.post<{ email: string; temporary_password: string; created: boolean;
               note: string }>(
      `/external-master/${partyId}/portal-access`, { email }),
  revokePortalAccess: (partyId: number) =>
    api.delete(`/external-master/${partyId}/portal-access`),

};
export interface CustomDetermination {
  id: number;
  key: string;
  label: string;
  unit: string | null;
  description: string | null;
  expression: any;
  enabled: boolean;
  updated_at: string | null;
}
export interface CustomDeterminationUpsert {
  key: string;
  label: string;
  unit?: string;
  description?: string;
  expression: any;
  enabled?: boolean;
}

export interface CustomCheckUpsert {
  finding_key: string;
  label: string;
  category?: string;
  severity?: string;
  description?: string;
  condition: RuleCondition;
  enabled?: boolean;
}

export interface HistoricalTestResult {
  tested: number;
  matched: number;
  results: Array<{ document_id: number; matched: boolean }>;
  samples: Array<{
    document_id: number;
    file_name: string | null;
    trace: Array<{ field: string; operator: string; expected: any; actual: any; result: boolean }>;
    decision: RuleTestResult['decision'];
  }>;
}

export interface RuleUpsert {
  name: string;
  description?: string;
  rule_type?: string;
  priority?: number;
  specificity?: number;
  scope?: Record<string, any>;
  condition: RuleCondition;
  actions: RuleAction[];
  policy_pack_id?: number | null;
}

export const ruleStudioService = {
  list: () => api.get<{ rules: PolicyRule[] }>('/rules'),
  get: (id: number) => api.get<PolicyRule>(`/rules/${id}`),
  create: (body: RuleUpsert) => api.post<PolicyRule>('/rules', body),
  update: (id: number, body: Partial<RuleUpsert>) => api.put<PolicyRule>(`/rules/${id}`, body),
  remove: (id: number) => api.delete(`/rules/${id}`),
  publish: (id: number) => api.post<PolicyRule>(`/rules/${id}/publish`),
  retire: (id: number) => api.post<PolicyRule>(`/rules/${id}/retire`),
  versions: (id: number) =>
    api.get<{ versions: Array<{ version: number; compiled: any; created_at: string | null }> }>(
      `/rules/${id}/versions`,
    ),
  activity: (limit = 25) =>
    api.get<{ activity: AutomationActivity[] }>('/rules/activity', { params: { limit } }),
  health: () => api.get<{ rules: RuleHealth[] }>('/rules/health'),
  conflicts: () => api.get<{ conflicts: RuleConflict[] }>('/rules/conflicts'),
  findingTypes: () =>
    api.get<{ finding_types: FindingTypeInfo[]; severities: string[]; categories: string[] }>('/rules/finding-types'),
  updateFindingType: (key: string, body: FindingTypeOverrideBody) =>
    api.put(`/rules/finding-types/${encodeURIComponent(key)}`, body),
  resetFindingType: (key: string) =>
    api.delete(`/rules/finding-types/${encodeURIComponent(key)}`),
  fieldBindings: () =>
    api.get<{ inputs: ValidatorInput[] }>('/rules/field-bindings'),
  updateFieldBinding: (logicalKey: string, field_names: string[]) =>
    api.put(`/rules/field-bindings/${encodeURIComponent(logicalKey)}`, { field_names }),
  factFields: () => api.get<FactFields>('/rules/fact-fields'),
  determinations: () => api.get<{ determinations: CustomDetermination[] }>('/rules/custom-determinations'),
  createDetermination: (body: CustomDeterminationUpsert) =>
    api.post<CustomDetermination>('/rules/custom-determinations', body),
  updateDetermination: (id: number, body: CustomDeterminationUpsert) =>
    api.put<CustomDetermination>(`/rules/custom-determinations/${id}`, body),
  deleteDetermination: (id: number) => api.delete(`/rules/custom-determinations/${id}`),
  validateExpr: (expression: any) =>
    api.post<{ valid: boolean; errors: string[] }>('/rules/validate-expr', { expression }),
  customChecks: () => api.get<{ custom_checks: CustomCheck[] }>('/rules/custom-checks'),
  createCheck: (body: CustomCheckUpsert) => api.post<CustomCheck>('/rules/custom-checks', body),
  updateCheck: (id: number, body: CustomCheckUpsert) =>
    api.put<CustomCheck>(`/rules/custom-checks/${id}`, body),
  deleteCheck: (id: number) => api.delete(`/rules/custom-checks/${id}`),
  validate: (condition: RuleCondition, actions: RuleAction[]) =>
    api.post<{ valid: boolean; errors: string[] }>('/rules/validate', { condition, actions }),
  test: (body: { document_id: number; condition?: RuleCondition; actions?: RuleAction[]; name?: string }) =>
    api.post<RuleTestResult>('/rules/test', body),
  testHistorical: (body: { condition: RuleCondition; actions?: RuleAction[]; name?: string; limit?: number }) =>
    api.post<HistoricalTestResult>('/rules/test-historical', body),
  decisions: (documentId: number) =>
    api.get<{ decisions: any[] }>('/rules/decisions', { params: { document_id: documentId } }),
  resolutions: (documentId: number) =>
    api.get<{ resolutions: any[] }>('/rules/resolutions', { params: { document_id: documentId } }),
};

export interface AutomationDecision {
  id: number;
  outcome: string;
  rationale: string | null;
  actions: RuleAction[];
  target: { target?: string } | null;
  requires_human: boolean;
  matched_rules: Array<{ id: number; name: string; version: number }>;
  created_at: string | null;
}
export interface AutomationResolution {
  id: number;
  action_type: string;
  strategy_key: string;
  status: 'applied' | 'skipped' | 'pending_human' | 'failed';
  safety_level: number | null;
  changed_fields: Record<string, any> | null;
  result: { message?: string } | null;
  attempt_no: number;
  created_at: string | null;
}

// ── Arbitrator department routing ──
export interface RoutingQueueItem {
  document_id: number;
  tenant_seq?: number | null;
  file_name: string | null;
  organization_id: number | null;
  suggested_department_id: number | null;
  suggested_department_name: string | null;
  routing_confidence: number | null;
  status: string;
  // Held: the vendor has no department mapping, so the document is stopped and
  // nobody can approve it. These are the ones actually blocking work.
  held?: boolean;
  hold_reason?: string | null;
  vendor_key?: string | null;
  vendor_name?: string | null;
  vendor_already_mapped?: boolean;
  departments?: Array<{ id: number; name: string }>;
  // 'routing' = needs a department; 'automation' = a rule flagged the CONTENT
  // (price/tax) and someone must look and release it; 'unplaced' = suggestion.
  hold_kind?: 'routing' | 'automation' | 'unplaced';
  automation_hold?: boolean;
  automation_hold_reason?: string | null;
  flag_detail?: string | null;
}
export interface RoutingSuggestions {
  document_id: number;
  candidates: Array<{ department_id: number; name: string | null; confidence: number; source: string }>;
  departments: Array<{ id: number; name: string }>;
}
export interface FindingReview {
  document_id: number; document_no: number | null; document_name: string | null;
  organization_name: string | null; target: string | null; raised_at: string | null;
  findings: { type: string; severity: string; message: string | null }[];
}
export const routingService = {
  // Findings a rule sent to Approvals for this user (no Task any more).
  myReviews: () => api.get<{ reviews: FindingReview[] }>('/routing/reviews/mine'),
  actReview: (documentId: number, action: 'approve' | 'reject', note?: string) =>
    api.post<{ message: string }>(`/routing/reviews/${documentId}/act`, { action, note }),
  queue: () => api.get<{ queue: RoutingQueueItem[] }>('/routing/queue'),
  // Cheap by design: the sidebar polls this every minute, while /queue
  // resolves the party for every row.
  queueCount: () => api.get<{ count: number; held: number }>('/routing/queue/count'),
  suggestions: (documentId: number) =>
    api.get<RoutingSuggestions>(`/routing/${documentId}/suggestions`),
  assign: (documentId: number, departmentId: number, rememberVendor = false) =>
    api.post<{ remembered?: boolean; learned?: boolean }>(
      `/routing/${documentId}/assign`,
      { department_id: departmentId, remember_vendor: rememberVendor }),
  // Release a document a rule flagged (price / tax) so it can post again.
  clearHold: (documentId: number, note?: string) =>
    api.post<{ cleared: boolean; message: string }>(`/routing/${documentId}/clear-hold`, { note }),
};

// ─────────────────────────── Subscription & Plan ───────────────────────────
// Tenant-admin view of what the workspace bought: modules, the pack chosen per
// module, price, per-metric quota vs usage, and invoices.
export interface PlanPack {
  pack_id: number;
  code: string;
  name: string;
  users: number | null;
  entities: number | null;
  pages: number | null;
  monthly_price: number | null;
}

export interface PlanModule {
  addon_id: number;
  code: string;
  name: string;
  metrics: string[];
  monthly_price: number | null;
  pack: PlanPack | null;
  // pricing_model_v2: how this module is billed.
  pricing_mode?: 'page_plan' | 'per_user' | null;
  per_user_cost?: number | null;   // per_user modules: ₹ / seat / month
  user_count?: number | null;      // per_user modules: seats bought
  // Seats are a per-module pool (not a tenant-wide users cap).
  seats?: { used: number; quota: number | null };
}

// pricing_model_v2: the shared page tier (Doc Processing + Real Estate).
export interface PagePlanInfo {
  plan_id: number;
  code: string;
  name: string;
  pages: number | null;
  entities: number | null;
  users: number | null;
  monthly_price: number | null;
}

export interface PlanMetric {
  metric: 'users' | 'entities' | 'pages';
  /** Display name, when the metric key alone doesn't identify the pool — a
   *  per-user module's seats are labelled by module rather than "Users". */
  label?: string;
  quota: number | null;
  used: number;
  balance: number | null;
  pct: number | null;
  unlimited: boolean;
  period: 'month' | 'total';
}

export interface SubscriptionSummary {
  tenant_id: number;
  state: string;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  base_monthly_price: number | null;
  modules_monthly_total: number;
  addons_monthly_total: number;
  effective_monthly_price: number | null;
  billing_email: string | null;
  plan_metrics: PlanMetric[];
  modules: PlanModule[];
  addons: Array<{ addon_id: number; code: string; name: string; quantity: number; monthly_price: number | null }>;
  // pricing_model_v2: the shared page plan (Doc Processing + Real Estate), priced once.
  page_plan?: PagePlanInfo | null;
  page_plan_price?: number | null;
  // The page modules' shared seat pool, already de-duplicated server-side.
  // Each page module's own `seats` reports this SAME pool, so never add them
  // together — read this. Null when the tenant owns no page module.
  page_plan_seats?: { used: number; quota: number | null } | null;
}

export interface TenantInvoice {
  id: number;
  invoice_number: string;
  description: string | null;
  period_start: string | null;
  period_end: string | null;
  subtotal: number | null;
  discount_amount: number | null;
  gst_amount: number | null;
  total: number | null;
  currency: string;
  status: string;
  issue_date: string | null;
  due_date: string | null;
  paid_at: string | null;
}

// pricing_model_v2 upgrade catalogue: shared page-plan tiers + per-user modules.
export interface CatalogPagePlan {
  plan_id: number;
  code: string;
  name: string;
  pages: number | null;
  entities: number | null;
  users: number | null;
  monthly_price: number | null;
}
export interface CatalogPerUserModule {
  addon_id: number;
  code: string;
  name: string;
  description: string | null;
  per_user_cost: number | null;
  owned: boolean;
  user_count: number | null;
}
/** pricing_packs_v3 — an a-la-carte top-up on the shared pool. `grant_*` is
 *  what ONE unit buys (e.g. grant_pages 500), so the UI can show the block
 *  size without hardcoding it. `quantity` is what the tenant holds today. */
export interface CatalogAddon {
  addon_id: number;
  code: string;
  name: string;
  description: string | null;
  monthly_price: number | null;
  grant_pages: number | null;
  grant_entities: number | null;
  grant_users: number | null;
  quantity: number;
}

export interface AddonRequest {
  id: number;
  addon: {
    addon_id: number;
    name: string;
    monthly_price: number | null;
    grant_pages: number | null;
    grant_entities: number | null;
    grant_users: number | null;
  } | null;
  quantity: number;
  note: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requested_by_name: string | null;
  decision_note: string | null;
  decided_at: string | null;
  created_at: string;
}

export interface PlanCatalog {
  owns_page_module: boolean;
  current_page_plan_id: number | null;
  page_plans: CatalogPagePlan[];
  per_user_modules: CatalogPerUserModule[];
  addons: CatalogAddon[];
  /** Live usage, so a downgrade can be priced honestly: picking a tier whose
   *  `users` sits below `usage.users` leaves excess users that keep working but
   *  were never charged for. See PlanUpgradeModal's overage block. */
  usage: { users: number; entities: number; pages: number };
}

export interface ModulePackRequest {
  id: number;
  module: { addon_id: number; code: string; name: string } | null;
  kind: 'upgrade' | 'add_module' | 'page_plan' | 'seats';
  current_pack: PlanPack | null;
  requested_pack: PlanPack | null;
  // pricing_model_v2 targets
  requested_page_plan: CatalogPagePlan | null;
  requested_user_count: number | null;
  note: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requested_by_name: string | null;
  decision_note: string | null;
  decided_at: string | null;
  /** When an approved change takes effect — the end of the period already paid
   *  for. Null once applied, or when there was no period to wait for. */
  scheduled_for?: string | null;
  /** When it was actually applied. Null while still waiting. */
  applied_at?: string | null;
  created_at: string;
}

export const subscriptionService = {
  getSubscription: () => api.get<SubscriptionSummary>('/me/subscription'),
  getInvoices: () => api.get<TenantInvoice[]>('/me/invoices'),
  requestCancellation: (reason?: string) => api.post('/me/request-cancellation', { reason }),
  /** Start the way back from past_due or suspended: voids a bill for a period
   *  that has already elapsed and reissues for 30 days from today, at whatever
   *  plan the tenant is on now. Reachable while suspended by design. */
  resume: () => api.post<{
    ok: boolean;
    detail?: string;
    invoice: { invoice_number: string; total: number; due_date: string | null;
               period_start: string | null; period_end: string | null } | null;
  }>('/me/resume'),

  /** v2 catalogue: page-plan tiers + per-user modules, annotated with what this
   *  tenant owns today. */
  getCatalog: () => api.get<PlanCatalog>('/me/plan-catalog'),
  getRequests: () => api.get<ModulePackRequest[]>('/me/module-pack-requests'),

  /** Ask to move onto a different shared page-plan tier. Applies on approval —
   *  there is no payment gateway. */
  requestPagePlan: (pagePlanId: number, note?: string) =>
    api.post<ModulePackRequest>('/me/page-plan-requests', {
      requested_page_plan_id: pagePlanId,
      note: note || null,
    }),

  /** Ask to change the seat count on a per-user module (or add it). Applies on
   *  approval. */
  requestSeats: (moduleAddonId: number, userCount: number, note?: string) =>
    api.post<ModulePackRequest>('/me/seat-requests', {
      module_addon_id: moduleAddonId,
      requested_user_count: userCount,
      note: note || null,
    }),

  /** Ask for N units of an a-la-carte add-on (extra users / entities / page
   *  blocks). Granted on operator approval — there is no payment gateway, and
   *  re-requesting the same add-on amends the open request rather than
   *  queueing a second one. */
  requestAddon: (addonId: number, quantity: number, note?: string) =>
    api.post<AddonRequest>('/me/addon-requests', {
      addon_id: addonId,
      quantity,
      note: note || null,
    }),
  getAddonRequests: () => api.get<AddonRequest[]>('/me/addon-requests'),

  /** The invoice document is auth-gated, so a plain window.open would 401.
   *  Pull it as a blob with the bearer token attached, then open that. */
  openInvoice: async (invoiceId: number) => {
    const res = await api.get(`/me/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

// Regulatory — generic entity links (M:N). Dark behind REGULATORY_ENABLED.
export const entityLinkService = {
  // Everything linked to a node (both directions), with the other end + relation.
  node: (nodeType: string, nodeId: number) =>
    api.get<{ links: { id: number; direction: 'in' | 'out'; relation: string; other_type: string; other_id: number }[] }>(
      '/entity-links/node', { params: { node_type: nodeType, node_id: nodeId } }),
  // Change-impact: what a change to this node ripples into (graph walk), and
  // turning the impacted set into change-review work items.
  impact: (nodeType: string, nodeId: number, depth = 2) =>
    api.get<{ source: any; depth: number; count: number; impacted: any[]; by_type: Record<string, any[]> }>(
      '/entity-links/impact', { params: { node_type: nodeType, node_id: nodeId, depth } }),
  raiseFollowups: (body: { node_type: string; node_id: number; owner_user_id: number; verifier_user_id: number; note?: string; depth?: number; target_kinds?: string[] }) =>
    api.post<{ created_count: number; created: any[]; impacted_total: number; targeted: number }>('/entity-links/impact/followups', body),
};

// Regulatory — work-type registry (configurable task kinds). Dark behind REGULATORY_ENABLED.
export const workTypeService = {
  list: () => api.get<{ work_types: any[] }>('/work-types'),
  upsert: (body: { work_type: string; label: string; group_name?: string; field_schema?: any[]; closure_policy?: any; subtask_template?: any[]; is_global?: boolean }) =>
    api.post<any>('/work-types', body),
};

// Regulatory — generic records (config-driven record types). Dark behind REGULATORY_ENABLED.
export const recordService = {
  types: () => api.get<{ types: any[] }>('/records/types'),
  upsertType: (body: { type_key: string; label: string; icon?: string; field_schema?: any[]; layout?: any; lifecycle?: any; features?: any; is_global?: boolean }) =>
    api.post<any>('/records/types', body),
  list: (params?: { type_key?: string; status_filter?: string }) =>
    api.get<{ records: any[] }>('/records', { params }),
  get: (id: number) => api.get<any>(`/records/${id}`),
  create: (body: any) => api.post<any>('/records', body),
  patch: (id: number, body: any) => api.patch<any>(`/records/${id}`, body),
  requestApproval: (id: number, body: { approver_user_id: number; verifier_user_id: number; note?: string }) =>
    api.post<{ approval_task_id: number; record_status: string; approved_status?: string }>(`/records/${id}/request-approval`, body),
  activity: (id: number) => api.get<{ activity: any[] }>(`/records/${id}/activity`),
  renderPdf: (id: number) => api.post<{ document_id: number }>(`/records/${id}/render-pdf`),
};

// Regulatory — dossiers (structured artifact: sections + compile + PDF). Dark behind REGULATORY_ENABLED.
export const dossierService = {
  list: () => api.get<{ dossiers: any[] }>('/dossiers'),
  create: (body: { title: string; template_key?: string; case_task_id?: number }) => api.post<any>('/dossiers', body),
  get: (id: number) => api.get<any>(`/dossiers/${id}`),
  patchSection: (did: number, sid: number, body: { document_id?: number | null; clear_document?: boolean; status?: string; responsible_user_id?: number; responsible_email?: string; reviewer_user_id?: number; reviewer_email?: string; notes?: string }) =>
    api.patch<any>(`/dossiers/${did}/sections/${sid}`, body),
  compile: (id: number) => api.post<any>(`/dossiers/${id}/compile`),
  download: (id: number) => api.get(`/dossiers/${id}/download`, { responseType: 'blob' }),
  templates: () => api.get<{ templates: any[] }>('/dossiers/templates'),
  createTemplate: (body: { template_key: string; label: string; sections: { name: string; required: boolean }[] }) =>
    api.post<any>('/dossiers/templates', body),
};

export const submissionService = {
  list: (statusFilter?: string) => api.get<{ submissions: any[] }>('/submissions', { params: statusFilter ? { status_filter: statusFilter } : undefined }),
  create: (body: { authority?: string; submission_type?: string; reference_number?: string; case_task_id?: number; organization_id?: number }) =>
    api.post<any>('/submissions', body),
  get: (id: number) => api.get<any>(`/submissions/${id}`),
  submit: (id: number, version?: string) => api.post<any>(`/submissions/${id}/submit`, undefined, { params: version ? { version } : undefined }),
  openRound: (id: number, body: { query_text?: string; owner_user_id: number; verifier_user_id: number; due_date?: string }) =>
    api.post<any>(`/submissions/${id}/rounds`, body),
  decision: (id: number, body: { decision: string; outcome?: string }) => api.post<any>(`/submissions/${id}/decision`, body),
};
